import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import moment from 'moment-timezone';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription
} from '../../components/ui/dialog';
import EditImgModal from '../../components/EditImgModal';
import { v4 as uuidv4 } from 'uuid';
// image-size/http/url removed — not compatible with browser/webpack 5
import { MANIPULATE_DESCRIPTION } from '../../utils';
import { authSelector } from '../../store/slices/auth';
import { selectedClientSelector } from '../../store/slices/selectedClient';
import { newPostSelector, clearNewPostData } from '../../store/slices/newPost';

import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import { Checkbox } from '../../components/ui/checkbox';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';

import Clock from 'react-live-clock';
import { cloneDeep } from 'lodash';
import {
  API_POSTS,
  API_ASSETS,
  API_POSTS_EDIT,
  API_POSTS_REPAIR,
  S3_GET_PRESIGNED_URLS,
  GENERATE_TEXT_FROM_IMAGE,
  FLYER_FEATURE_ENABLED,
  TIKTOK_ENABLED,
  TIKTOK_CREATOR_INFO_URL,
  TIKTOK_OAUTH_START_URL,
  AI_VIDEO_GENERATION_API_KEY
} from '../../utils';

import './NewPost.scss';
import NewPostSkeleton from './NewPostSkeleton';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger
} from '../../components/ui/tabs';
import { SearchContext } from '../../contexts/SearchContext';

import UnifiedImageGallery from '../Calendar/GallerySection/UnifiedImageGallery';
import OfferModal from '../../components/NewPost/OfferPost/OfferModal';
import SocialPostForm from './SocialPostForm';
import CreateFlyerCard from '../../components/NewPost/CreateFlyerCard';

import {
  ArrowLeft,
  ArrowRight,
  Check,
  Save,
  FileText,
  Tag,
  FileImage,
  Grid3X3,
  Clock as ClockIcon,
  Video,
  Link as LinkIcon,
  Upload,
  Sparkles,
  ChevronDown
} from 'lucide-react';

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from '../../components/ui/card';

import { Separator } from '../../components/ui/separator';
import { ToggleGroup, ToggleGroupItem } from '../../components/ui/toggle-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../../components/ui/select';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from '../../components/ui/collapsible';
import DateTimePicker from 'react-datetime-picker';
import getCroppedImg from '../../components/EditImgModal/cropImage';

const reactStringReplace = require('react-string-replace');
const axios = require('axios');
const AI_VIDEO_DEFAULT_DURATION_SEC = 140;
const AI_VIDEO_SCRIPT_MIN_CHARS = 100;
const AI_VIDEO_SCRIPT_MAX_CHARS = 150;

const NewPost = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { user } = useUser();

  // Redux selectors
  const { userInfo } = useSelector(authSelector);
  const { selectedClient } = useSelector(selectedClientSelector);
  const newPostData = useSelector(newPostSelector);
  const postsListsData = useSelector((state) => state.posts?.postsLists || []);

  // Check if user has "pro" plan in public metadata
  const hasProPlan = user?.publicMetadata?.plan === 'pro';

  // Determine if user is superAdmin based on email domain
  const userEmail = user?.primaryEmailAddress?.emailAddress || '';
  const isSuperAdmin =
    userEmail.endsWith('@adlerlocal.com') ||
    userEmail.endsWith('@samxlabs.com') ||
    userEmail.endsWith('@adlersocial.com');

  // Helper function to check if an object has valid content
  const hasValidData = (obj) => {
    return obj && typeof obj === 'object' && Object.keys(obj).length > 0;
  };

  // Verificar si hay un cliente válido al cargar
  useEffect(() => {
    // Si no hay cliente seleccionado y no hay datos en Redux, redirigir al calendario
    if (!selectedClient?.id && !newPostData.currentClientId) {
      history.replace('/calendar');
    }
  }, [selectedClient?.id, newPostData.currentClientId, history]);

  // Usar useMemo para estabilizar las referencias de objetos
  const props = useMemo(() => {
    // Determinar las redes sociales con prioridad: Redux > selectedClient
    let socialNetworks = {};
    let socialNetworksSource = 'empty';

    if (hasValidData(newPostData.currentClientSocialNetworks)) {
      socialNetworks = newPostData.currentClientSocialNetworks;
      socialNetworksSource = 'newPostData.currentClientSocialNetworks';
    } else if (hasValidData(selectedClient?.clientSocialNetworks)) {
      socialNetworks = selectedClient.clientSocialNetworks;
      socialNetworksSource = 'selectedClient.clientSocialNetworks';
    } else if (hasValidData(selectedClient?.socialnetworks)) {
      socialNetworks = selectedClient.socialnetworks;
      socialNetworksSource = 'selectedClient.socialnetworks';
    }

    // Determinar best times con la misma lógica
    const bestTimes = hasValidData(newPostData.currentClientBestTimes)
      ? newPostData.currentClientBestTimes
      : hasValidData(selectedClient?.bestTimes)
      ? selectedClient.bestTimes
      : {};

    // Determinar merge fields con la misma lógica
    const mergeFields = hasValidData(newPostData.currentClientMergeFields)
      ? newPostData.currentClientMergeFields
      : hasValidData(selectedClient?.mergeFields)
      ? selectedClient.mergeFields
      : {};

    return {
      post: newPostData.post || null,
      show: newPostData.show !== undefined ? newPostData.show : true,
      hideRecurrent:
        newPostData.hideRecurrent !== undefined
          ? newPostData.hideRecurrent
          : true,
      isCalendar: newPostData.isCalendar || false,
      currentClientId: newPostData.currentClientId || selectedClient?.id,
      currentClientSocialNetworks: socialNetworks,
      currentClientSocialNetworksSource: socialNetworksSource,
      currentClientTimeZone:
        newPostData.currentClientTimeZone ||
        selectedClient?.timeZone ||
        'Eastern Daylight Time - (GMT-4)',
      currentClientBestTimes: bestTimes,
      currentClientMergeFields: mergeFields,
      currentCustomerName:
        newPostData.currentCustomerName || userInfo?.displayName,
      onClose: () => {
        dispatch(clearNewPostData());
        history.goBack();
      }
    };
  }, [newPostData, selectedClient, userInfo, dispatch, history]);

  const [date, setDate] = useState(null);
  const [post, setPost] = useState({
    socialNetworks: {
      facebook: false,
      twitter: false,
      googleMyBusiness: false,
      instagram: false
    }
  });
  const [selectedImagePK, setSelectedImagePK] = useState(null);
  const [selectedImageSK, setSelectedImageSK] = useState(null);
  const [assetUrl, setAssetUrl] = useState({
    value: ''
  });

  const [savingPost, setSavingPost] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [savingTikTokDraft, setSavingTikTokDraft] = useState(false);
  const [repairingPost, setRepairingPost] = useState(false);
  const [preview, setPreview] = useState(null);
  const [showRecurrent, setShowRecurrent] = useState(false);
  const [checked, setChecked] = useState(false);
  const [recurrentDates, setRecurrentDates] = useState([]);
  const [isCustomTime, setIsCustomTime] = useState(false);
  const [postDescription, setPostDescription] = useState('');
  const [showEmojisMenu, setShowEmojisMenu] = useState(false);
  const [showImgEditModal, setShowImgEditModal] = useState(false);
  const [croppedImg, setCroppedImg] = useState(null);
  const [isGeneratingText, setIsGeneratingText] = useState(false);
  const [hasSelectedImage, setHasSelectedImage] = useState(false);

  // Modal states
  const [imageTooSmallModalOpen, setImageTooSmallModalOpen] = useState(false);
  const [imageDimensions, setImageDimensions] = useState({
    width: 0,
    height: 0
  });
  const [postSentSuccessModalOpen, setPostSentSuccessModalOpen] =
    useState(false);
  const [postErrorModalOpen, setPostErrorModalOpen] = useState(false);
  const [noMessageErrorModalOpen, setNoMessageErrorModalOpen] = useState(false);
  const [messageValidationErrorModalOpen, setMessageValidationErrorModalOpen] =
    useState(false);
  const [validationErrorMessage, setValidationErrorMessage] = useState('');
  const [noImageErrorModalOpen, setNoImageErrorModalOpen] = useState(false);
  const [invalidUrlErrorModalOpen, setInvalidUrlErrorModalOpen] =
    useState(false);
  const [noImageSelectedErrorModalOpen, setNoImageSelectedErrorModalOpen] =
    useState(false);
  const [textGeneratedSuccessModalOpen, setTextGeneratedSuccessModalOpen] =
    useState(false);
  const [
    aiVideoGeneratedSuccessModalOpen,
    setAiVideoGeneratedSuccessModalOpen
  ] = useState(false);
  const [textGenerationErrorModalOpen, setTextGenerationErrorModalOpen] =
    useState(false);
  const [, setTextAreaCursorPostion] = useState({
    selectionStart: 0,
    selectionEnd: 0
  });
  const [allUrls, setAllUrls] = useState([]);
  const [des, setDes] = useState(null);
  const [urlImg, setUrlImg] = useState(null);
  const [refresh, setRefresh] = useState(false);
  const [, setTagsig] = useState('');
  const [imgurlig, setImgurlig] = useState('');
  const assetAttached = useRef(null);
  const [bestTime, setBestTime] = useState(null);
  const modalDismiss = useRef(null);
  const resetForm = useRef(null);
  const message = useRef(null);
  const checkFb = useRef(null);
  const checkGmb = useRef(null);
  const checkTw = useRef(null);
  const checkIg = useRef(null);
  const [query, setQuery] = useState('');
  const [isPromotional, setIsPromotional] = useState(false);
  const [isOffer, setIsOffer] = useState(false);
  const [postType, setPostType] = useState('social');
  const [tiktokCaption, setTiktokCaption] = useState('');
  const [tiktokVideoUrl, setTiktokVideoUrl] = useState('');
  const [tiktokVideoPreviewUrl, setTiktokVideoPreviewUrl] = useState('');
  const [tiktokVideoFile, setTiktokVideoFile] = useState(null);
  const [isUploadingTikTokVideo, setIsUploadingTikTokVideo] = useState(false);
  const [tiktokSourceType, setTiktokSourceType] = useState('video-url');
  const [, setIsOriginalVisible] = useState(true);
  const [isGeneratingTiktokCaption, setIsGeneratingTiktokCaption] =
    useState(false);
  const [, setTiktokCaptionGenerationError] = useState('');

  // TikTok Direct Post API states
  const [tiktokCreatorInfo, setTiktokCreatorInfo] = useState(null);
  const [tiktokCreatorInfoLoading, setTiktokCreatorInfoLoading] =
    useState(false);
  const [tiktokCreatorInfoError, setTiktokCreatorInfoError] = useState('');
  const [tiktokCreatorInfoStatus, setTiktokCreatorInfoStatus] = useState('');
  const [tiktokReconnectRequired, setTiktokReconnectRequired] = useState(false);
  const [tiktokPrivacyLevel, setTiktokPrivacyLevel] =
    useState('PUBLIC_TO_EVERYONE');
  const [tiktokAllowComment, setTiktokAllowComment] = useState(true);
  const [tiktokAllowDuet, setTiktokAllowDuet] = useState(true);
  const [tiktokAllowStitch, setTiktokAllowStitch] = useState(true);
  const [tiktokDisclosureEnabled, setTiktokDisclosureEnabled] = useState(false);
  const [tiktokYourBrand, setTiktokYourBrand] = useState(false);
  const [tiktokBrandedContent, setTiktokBrandedContent] = useState(false);
  const [tiktokMusicUsageConfirmed, setTiktokMusicUsageConfirmed] =
    useState(false);
  const [tiktokIsAigc, setTiktokIsAigc] = useState(false);
  const [tiktokVideoDurationSec, setTiktokVideoDurationSec] = useState(null);
  const [createFlyerMode, setCreateFlyerMode] = useState(false);

  // AI Video Generation states
  const [aiVideoMode, setAiVideoMode] = useState(false);
  const [aiImageUrl, setAiImageUrl] = useState('');
  const [aiScript, setAiScript] = useState('');
  const [aiCta, setAiCta] = useState('Visit Us Today!');
  const [aiPhone, setAiPhone] = useState('');
  const [aiWebsite, setAiWebsite] = useState('');
  const [aiUseLogo, setAiUseLogo] = useState(false);
  const [aiLogoUrl, setAiLogoUrl] = useState('');
  const [aiVideoGenerationJobId, setAiVideoGenerationJobId] = useState(null);
  const [aiVideoGenerationStatus, setAiVideoGenerationStatus] = useState(null);
  const [aiVideoGeneratedUrl, setAiVideoGeneratedUrl] = useState(null);
  const [aiVideoGenerationRequestId, setAiVideoGenerationRequestId] =
    useState('');
  const [isGeneratingAiVideo, setIsGeneratingAiVideo] = useState(false);
  const [aiGenerationStartAtMs, setAiGenerationStartAtMs] = useState(null);
  const [aiLastRealDurationSec, setAiLastRealDurationSec] = useState(null);
  const [aiFakeDurationSec, setAiFakeDurationSec] = useState(
    AI_VIDEO_DEFAULT_DURATION_SEC + 20
  );
  const [aiCountdownSec, setAiCountdownSec] = useState(
    AI_VIDEO_DEFAULT_DURATION_SEC + 20
  );
  const [aiElapsedSec, setAiElapsedSec] = useState(0);
  const [tiktokSettingsOpen, setTiktokSettingsOpen] = useState(false);
  const [aiVoiceId, setAiVoiceId] = useState('female');
  const [isGeneratingAiScript, setIsGeneratingAiScript] = useState(false);

  const [postErrorMessage, setPostErrorMessage] = useState(
    'We have trouble adding the publication'
  );

  let tagsPhrasesRef = useRef(null);
  const postIdRef = useRef(null);
  const offerModalRef = useRef(null);
  const tiktokVideoInputRef = useRef(null);
  const aiPollIntervalRef = useRef(null);
  const aiFakeTimerIntervalRef = useRef(null);
  const activeAiGenerationTokenRef = useRef(null);

  let days = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday'
  ];

  const isTikTokPost = TIKTOK_ENABLED && postType === 'tiktok';
  const isSocialPost = postType === 'social';
  const isTikTokConnectedForClient = Boolean(
    props.currentClientSocialNetworks?.tiktok ||
      selectedClient?.socialnetworks?.tiktok ||
      selectedClient?.socialNetworks?.tiktok
  );
  const isEditingExistingTikTokPost = Boolean(
    props.post?.id &&
      (props.post?.socialNetworks?.tiktok ||
        props.post?.tiktok?.videoUrl ||
        props.post?.platform === 'tiktok')
  );
  const showTikTokPostTypeOption =
    TIKTOK_ENABLED &&
    (isTikTokConnectedForClient || isEditingExistingTikTokPost || isTikTokPost);
  const shouldDisableTikTokPosting =
    !hasProPlan ||
    !TIKTOK_ENABLED ||
    (!isTikTokConnectedForClient &&
      !isEditingExistingTikTokPost &&
      !isTikTokPost);
  const aiScriptLength = (aiScript || '').trim().length;
  const hasAiScriptForCaption = aiScriptLength > 0;
  const isAiScriptLengthValid =
    aiScriptLength >= AI_VIDEO_SCRIPT_MIN_CHARS &&
    aiScriptLength <= AI_VIDEO_SCRIPT_MAX_CHARS;
  const aiGenerationLockActive = aiVideoMode && isGeneratingAiVideo;

  const isTikTokConnectionBlocked =
    tiktokReconnectRequired ||
    tiktokCreatorInfoStatus === 'NOT_CONNECTED' ||
    tiktokCreatorInfoStatus === 'TIKTOK_RECONNECT_REQUIRED';

  // Disable Publish/Post as TikTok Draft when required Direct Post fields are missing
  const tiktokDirectPostDisabled =
    isTikTokPost &&
    (tiktokCreatorInfoLoading ||
      isTikTokConnectionBlocked ||
      !tiktokCreatorInfo ||
      !tiktokPrivacyLevel ||
      !tiktokMusicUsageConfirmed ||
      (tiktokDisclosureEnabled && !tiktokYourBrand && !tiktokBrandedContent) ||
      (tiktokBrandedContent && tiktokPrivacyLevel === 'SELF_ONLY'));

  useEffect(() => {
    const socialNetworks = props.currentClientSocialNetworks || {};
    const clientFlags = selectedClient?.clientFlags || [];
    const isFlagSet = (flag) =>
      Array.isArray(clientFlags)
        ? clientFlags.includes(flag)
        : !!clientFlags?.[flag];
    const computedAvailability = {
      facebook: Boolean(socialNetworks.facebook) && !isFlagSet('NO_FB'),
      instagram: Boolean(socialNetworks.instagram) && !isFlagSet('NO_IG'),
      googleMyBusiness:
        Boolean(socialNetworks.googleMyBusiness) && !isFlagSet('NO_GMB'),
      twitter: Boolean(socialNetworks.twitter)
    };

    console.groupCollapsed('[NewPost][PostTo diagnostics]');
    console.log('client', {
      id: selectedClient?.id || props.currentClientId || null,
      clientName: selectedClient?.clientName || null,
      appUid: selectedClient?.appUid || null
    });
    console.log(
      'social network source',
      props.currentClientSocialNetworksSource
    );
    console.log('newPostData.currentClientSocialNetworks', {
      keys: Object.keys(newPostData.currentClientSocialNetworks || {}),
      value: newPostData.currentClientSocialNetworks || {}
    });
    console.log('selectedClient.clientSocialNetworks', {
      keys: Object.keys(selectedClient?.clientSocialNetworks || {}),
      value: selectedClient?.clientSocialNetworks || {}
    });
    console.log('selectedClient.socialnetworks', {
      keys: Object.keys(selectedClient?.socialnetworks || {}),
      value: selectedClient?.socialnetworks || {}
    });
    console.log('clientFlags', clientFlags);
    console.log('computed availability for Post to', computedAvailability);
    console.log(
      'will show "No social networks available"',
      !computedAvailability.facebook &&
        !computedAvailability.instagram &&
        !computedAvailability.googleMyBusiness &&
        !computedAvailability.twitter
    );
    console.groupEnd();
  }, [
    newPostData.currentClientSocialNetworks,
    props.currentClientId,
    props.currentClientSocialNetworks,
    props.currentClientSocialNetworksSource,
    selectedClient?.appUid,
    selectedClient?.clientFlags,
    selectedClient?.clientName,
    selectedClient?.clientSocialNetworks,
    selectedClient?.id,
    selectedClient?.socialnetworks
  ]);

  useEffect(() => {
    if (savingPost || savingDraft || repairingPost || savingTikTokDraft) {
      if (isTikTokPost) {
        submitTikTokPost();
      } else {
        savePost();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savingDraft, savingPost, repairingPost, isTikTokPost, savingTikTokDraft]);

  useEffect(() => {
    return () => {
      if (tiktokVideoPreviewUrl && tiktokVideoPreviewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(tiktokVideoPreviewUrl);
      }
    };
  }, [tiktokVideoPreviewUrl]);

  useEffect(() => {
    return () => {
      if (aiPollIntervalRef.current) {
        clearInterval(aiPollIntervalRef.current);
      }
      if (aiFakeTimerIntervalRef.current) {
        clearInterval(aiFakeTimerIntervalRef.current);
      }
      activeAiGenerationTokenRef.current = null;
    };
  }, []);

  const handleReconnectTikTok = () => {
    if (!props.currentClientId) return;
    const query = new URLSearchParams({
      clientId: String(props.currentClientId),
      returnTo: '/new-post'
    }).toString();
    window.location.href = `${TIKTOK_OAUTH_START_URL}?${query}`;
  };

  // Load TikTok creator info when switching to TikTok post type
  useEffect(() => {
    if (!isTikTokPost || !props.currentClientId) {
      return;
    }
    let cancelled = false;
    const fetchCreatorInfo = async () => {
      setTiktokCreatorInfoLoading(true);
      setTiktokCreatorInfoError('');
      setTiktokCreatorInfoStatus('');
      setTiktokReconnectRequired(false);
      setTiktokCreatorInfo(null);
      try {
        const response = await axios.post(TIKTOK_CREATOR_INFO_URL, {
          clientId: props.currentClientId
        });
        if (cancelled) return;
        const data = response.data;
        if (data?.success === true && data?.creator) {
          setTiktokCreatorInfo(data.creator);
          return;
        }
        const status = data?.status || 'UNKNOWN_ERROR';
        const errorMsg =
          data?.message ||
          (status === 'TIKTOK_RECONNECT_REQUIRED' || status === 'NOT_CONNECTED'
            ? 'Your TikTok connection expired. Please reconnect TikTok before posting.'
            : 'Could not load TikTok account information. Please try again.');
        setTiktokCreatorInfoStatus(status);
        setTiktokCreatorInfoError(errorMsg);
        if (
          status === 'TIKTOK_RECONNECT_REQUIRED' ||
          status === 'NOT_CONNECTED'
        ) {
          setTiktokReconnectRequired(true);
        } else {
          setPostErrorMessage(errorMsg);
          setPostErrorModalOpen(true);
        }
      } catch (err) {
        if (cancelled) return;
        const data = err?.response?.data;
        const status = data?.status || '';
        const isNetworkError = !err?.response;
        const rawMessage = data?.message;
        const errorMsg =
          rawMessage ||
          (isNetworkError
            ? 'Unable to load your TikTok account. This may be a connection issue or your session may have expired. Please try again or reconnect TikTok.'
            : status === 'TIKTOK_RECONNECT_REQUIRED' ||
              status === 'NOT_CONNECTED'
            ? 'Your TikTok connection expired. Please reconnect TikTok before posting.'
            : 'Could not load TikTok account information. Please try again.');
        setTiktokCreatorInfoStatus(status);
        setTiktokCreatorInfoError(errorMsg);
        if (
          status === 'TIKTOK_RECONNECT_REQUIRED' ||
          status === 'NOT_CONNECTED'
        ) {
          setTiktokReconnectRequired(true);
        } else {
          setPostErrorMessage(errorMsg);
          setPostErrorModalOpen(true);
        }
      } finally {
        if (!cancelled) setTiktokCreatorInfoLoading(false);
      }
    };
    fetchCreatorInfo();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTikTokPost, props.currentClientId]);

  // Auto-activate AI content checkbox when AI video mode is on
  useEffect(() => {
    if (aiVideoMode) {
      setTiktokIsAigc(true);
    }
  }, [aiVideoMode]);

  // Auto-fill client info (phone, website) when AI video mode is enabled
  useEffect(() => {
    if (aiVideoMode && props.currentClientMergeFields) {
      // Fill phone from merge fields if not already filled
      if (!aiPhone && props.currentClientMergeFields['~Phone']) {
        setAiPhone(props.currentClientMergeFields['~Phone']);
      }
      // Fill website from merge fields if not already filled
      if (!aiWebsite && props.currentClientMergeFields['~Website']) {
        setAiWebsite(props.currentClientMergeFields['~Website']);
      }

      // Preload logo from client if available
      const clientLogo =
        selectedClient?.logoUrl || newPostData.currentClientLogoUrl;
      if (clientLogo) {
        setAiLogoUrl(clientLogo);
        setAiUseLogo(true);
      } else {
        setAiUseLogo(false);
      }
    }
  }, [
    aiVideoMode,
    props.currentClientMergeFields,
    aiPhone,
    aiWebsite,
    selectedClient?.logoUrl,
    newPostData.currentClientLogoUrl
  ]);

  // Auto-activate music confirmation when video and caption are present
  useEffect(() => {
    const hasVideoSource = Boolean(
      tiktokVideoUrl ||
        aiVideoGeneratedUrl ||
        tiktokVideoPreviewUrl ||
        tiktokVideoFile
    );
    const hasCaption = tiktokCaption.trim().length > 0;

    if (isTikTokPost && hasVideoSource && hasCaption) {
      setTiktokMusicUsageConfirmed(true);
    }
  }, [
    isTikTokPost,
    tiktokVideoUrl,
    aiVideoGeneratedUrl,
    tiktokVideoPreviewUrl,
    tiktokVideoFile,
    tiktokCaption
  ]);

  // Efecto separado para inicializar el formulario basado en props.post
  useEffect(() => {
    // Solo ejecutar si hay un post nuevo o el post ha cambiado
    const currentPostId = props.post?.id || props.post?.start;
    if (!props.post || currentPostId === postIdRef.current) {
      return;
    }

    postIdRef.current = currentPostId;

    if (props.post && !props.post.status) {
      if (
        props.post.start.getDate() ===
        getTime(props.currentClientTimeZone).getDate()
      )
        setDate(getTime(props.currentClientTimeZone));
      else {
        setDate(props.post.start);
      }
    } else if (props.post && props.post.status) {
      setIsCustomTime(true);

      if (typeof props.post.start === 'number') {
        setDate(new Date(props.post.start * 1000));
      } else {
        setDate(new Date(props.post.start));
      }

      let post = cloneDeep(props.post);
      let start = props.post ? new Date(props.post.start * 1000) : new Date();

      if (typeof start == 'number') {
        start = new Date(start * 1000);
      }

      const isTikTokPostData = Boolean(
        post?.socialNetworks?.tiktok ||
          post?.tiktok?.videoUrl ||
          post?.tiktok?.status ||
          post?.platform === 'tiktok'
      );

      // Detect TikTok-only posts and preload TikTok composer state
      if (isTikTokPostData) {
        setPostType('tiktok');
        setTiktokCaption(post.title || '');
        setTiktokSourceType('video-url');
        setTiktokVideoFile(null);
        setTiktokVideoUrl(post?.tiktok?.videoUrl || '');
        setTiktokVideoPreviewUrl(post?.tiktok?.videoUrl || '');
        setTiktokPrivacyLevel(post?.tiktok?.privacyLevel || '');
        setTiktokAllowComment(Boolean(post?.tiktok?.allowComment));
        setTiktokAllowDuet(Boolean(post?.tiktok?.allowDuet));
        setTiktokAllowStitch(Boolean(post?.tiktok?.allowStitch));
        setTiktokDisclosureEnabled(
          Boolean(post?.tiktok?.commercialContentDisclosure?.enabled)
        );
        setTiktokYourBrand(
          Boolean(post?.tiktok?.commercialContentDisclosure?.yourBrand)
        );
        setTiktokBrandedContent(
          Boolean(post?.tiktok?.commercialContentDisclosure?.brandedContent)
        );
        setTiktokMusicUsageConfirmed(
          Boolean(post?.tiktok?.musicUsageConfirmed)
        );
        setTiktokIsAigc(Boolean(post?.tiktok?.isAigc));

        setPost((prevPost) => ({
          ...prevPost,
          ...post,
          socialNetworks: {
            facebook: false,
            twitter: false,
            googleMyBusiness: false,
            instagram: false,
            tiktok: true,
            ...(post.socialNetworks || {})
          }
        }));

        if (message.current) {
          message.current.value = post.title || '';
        }

        setPostDescription(post.title || '');
        return;
      }

      if (message.current) {
        message.current.value = post['title'];
      }
      setPostDescription(post.title);
      setAssetUrl({
        value: post['images'] !== undefined ? post['images'][0]['url'] : ''
      });

      let { googleMyBusiness, facebook, twitter, instagram } = false;

      if (props.post && props.post.socialNetworks) {
        const { hashtagsig } = props.post;
        if (hashtagsig) {
          setTagsig(hashtagsig);
        }
        if (props.post.socialNetworks.googleMyBusiness) {
          googleMyBusiness = true;
        }
        if (props.post.socialNetworks.facebook) {
          facebook = true;
        }
        if (props.post.socialNetworks.twitter) {
          twitter = true;
        }
        if (props.post.socialNetworks.instagram) {
          instagram = true;
        }
      } else {
        if (props.currentClientSocialNetworks.googleMyBusiness) {
          googleMyBusiness = true;
        }
        if (props.currentClientSocialNetworks.facebook) {
          facebook = true;
        }
        if (props.currentClientSocialNetworks.twitter) {
          twitter = true;
        }
        if (props.currentClientSocialNetworks.instagram) {
          instagram = true;
        }
      }

      post['socialNetworks'] = {
        googleMyBusiness,
        facebook,
        twitter,
        instagram
      };
      setPost(post);

      if (date && !isCustomTime) {
        setDate(
          new Date(
            start.setHours(
              parseInt(props.currentClientBestTimes[days[start.getDay()]]),
              Math.random() * 59
            )
          )
        );
      }
      if (
        date !== null &&
        date !== props.post.start &&
        props.post.title === ''
      ) {
        setIsCustomTime(false);
      }

      post['images'] ? setPreview(post['images'][0]['url']) : setPreview('');
    } else if (
      !props.isCalendar &&
      props.show &&
      (date === null || bestTime === null) &&
      props.currentClientId
    ) {
      let noCalendarDate = getTime(props.currentClientTimeZone);
      setDate(getTime(props.currentClientTimeZone));
      setBestTime(
        new Date(
          noCalendarDate.setHours(
            parseInt(
              props.currentClientBestTimes[days[noCalendarDate.getDay()]]
            ),
            Math.random() * 59
          )
        )
      );
    }
    if (bestTime === null && props.post) {
      let start = props.post ? props.post.start : new Date();
      if (typeof start === 'number') {
        start = new Date(start);
      }

      // Clone the date to avoid mutating props.post.start (which would corrupt the `date` state)
      let temp_date = new Date(start);
      const dayName = days[temp_date.getDay()];
      const bestHour = parseInt(props.currentClientBestTimes?.[dayName]);
      if (!isNaN(bestHour)) {
        temp_date.setHours(bestHour, Math.floor(Math.random() * 59));
      }
      setBestTime(temp_date);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.post?.id, props.post?.start]);

  useEffect(() => {
    const googleMyBusiness =
      props.currentClientSocialNetworks.googleMyBusiness || false;
    const facebook = props.currentClientSocialNetworks.facebook || false;
    const twitter = props.currentClientSocialNetworks.twitter || false;
    const instagram = props.currentClientSocialNetworks.instagram || false;

    setPost((prevPost) => ({
      ...prevPost,
      socialNetworks: {
        googleMyBusiness,
        facebook,
        twitter,
        instagram
      }
    }));
  }, [props.currentClientSocialNetworks]);

  const checkMessageAndReplaceTags = (message) => {
    let newMessage = message;
    let error = '';
    let mergeFields = props.currentClientMergeFields;

    if (/~Name/i.test(newMessage)) {
      if (mergeFields['~Name'].trim().length === 0) {
        error = 'The ~Name tag has not been assigned.';
        return { status: true, message: error };
      } else {
        reactStringReplace(
          newMessage,
          /(~Name)/i,
          (match) =>
            (newMessage = newMessage.replace(match, mergeFields['~Name']))
        );
      }
    }

    if (/~Address/i.test(newMessage)) {
      if (mergeFields['~Address'].trim().length === 0) {
        error = 'The ~Address tag has not been assigned.';
        return { status: true, message: error };
      } else {
        reactStringReplace(
          newMessage,
          /(~Address)/i,
          (match) =>
            (newMessage = newMessage.replace(match, mergeFields['~Address']))
        );
      }
    }

    if (/~City/i.test(newMessage)) {
      if (mergeFields['~City'].trim().length === 0) {
        error = 'The ~City tag has not been assigned.';
        return { status: true, message: error };
      } else {
        reactStringReplace(
          newMessage,
          /(~City)/i,
          (match) =>
            (newMessage = newMessage.replace(match, mergeFields['~City']))
        );
      }
    }

    if (/~Phone/i.test(newMessage)) {
      if (mergeFields['~Phone'].trim().length === 0) {
        error = 'The ~Phone tag has not been assigned.';
        return { status: true, message: error };
      } else {
        reactStringReplace(
          newMessage,
          /(~Phone)/i,
          (match) =>
            (newMessage = newMessage.replace(match, mergeFields['~Phone']))
        );
      }
    }

    if (/~Website/i.test(newMessage)) {
      if (
        typeof mergeFields['~Website'] == 'undefined' ||
        mergeFields['~Website'].trim().length === 0
      ) {
        error = 'The ~Website tag has not been assigned.';
        return { status: true, message: error };
      } else {
        reactStringReplace(
          newMessage,
          /(~Website)/i,
          (match) =>
            (newMessage = newMessage.replace(match, mergeFields['~Website']))
        );
      }
    }

    if (/~BestDish/i.test(newMessage)) {
      if (mergeFields['~BestDish'].trim().length === 0) {
        error = 'The ~BestDish tag has not been assigned.';
        return { status: true, message: error };
      } else {
        reactStringReplace(
          newMessage,
          /(~BestDish)/i,
          (match) =>
            (newMessage = newMessage.replace(match, mergeFields['~BestDish']))
        );
      }
    }

    if (/~BestService/i.test(newMessage)) {
      if (mergeFields['~BestService'].trim().length === 0) {
        error = 'The ~BestService tag has not been assigned.';
        return { status: true, message: error };
      } else {
        reactStringReplace(
          newMessage,
          /(~BestService)/i,
          (match) =>
            (newMessage = newMessage.replace(
              match,
              mergeFields['~BestService']
            ))
        );
      }
    }

    if (/~BestProduct/i.test(newMessage)) {
      if (mergeFields['~BestProduct'].trim().length === 0) {
        error = 'The ~BestProduct tag has not been assigned.';
        return { status: true, message: error };
      } else {
        reactStringReplace(
          newMessage,
          /(~BestProduct)/i,
          (match) =>
            (newMessage = newMessage.replace(
              match,
              mergeFields['~BestProduct']
            ))
        );
      }
    }

    if (/~/.test(newMessage)) {
      error = 'Invalid ~ tag.';
      return { status: true, message: error };
    }

    return { status: false, message: newMessage };
  };

  const editTimeRecurrentDates = (RecurrentPosts, currentClientTimeZone) => {
    let timeZone = currentClientTimeZone;
    let timeZoneToUnix = moment(getTime(timeZone)).seconds(0).unix();
    let timeLocation = moment(new Date()).seconds(0).unix();
    const posts = RecurrentPosts.map((item) => {
      return Math.abs(timeZoneToUnix - item) + timeLocation;
    });

    return posts;
  };

  const saveCurrent = () => {
    setSavingPost(true);
  };

  const saveDraft = () => {
    setSavingDraft(true);
  };

  const repairPost = () => {
    setRepairingPost(true);
  };

  const resetComposerState = () => {
    clearAiGenerationIntervals();
    activeAiGenerationTokenRef.current = null;

    if (resetForm.current) resetForm.current.click();

    setDate(null);
    setPost({
      socialNetworks: {
        facebook: true,
        twitter: true,
        googleMyBusiness: true,
        instagram: true
      }
    });
    setAssetUrl({
      value: ''
    });
    setSavingPost(false);
    setSavingDraft(false);
    setSavingTikTokDraft(false);
    setRepairingPost(false);
    setPreview(null);
    setHasSelectedImage(false);
    setIsGeneratingText(false);
    setShowRecurrent(false);
    setChecked(false);
    setRecurrentDates([]);
    setIsCustomTime(false);
    setDate(null);
    setPostDescription('');
    setShowEmojisMenu(false);
    setShowImgEditModal(false);
    setCroppedImg(null);
    setTextAreaCursorPostion({
      selectionStart: 0,
      selectionEnd: 0
    });
    setAllUrls([]);
    setDes(null);
    setIsPromotional(false);
    setIsOffer(false);
    setPostType('social');
    setTiktokCaption('');
    setTiktokVideoUrl('');
    setTiktokVideoPreviewUrl('');
    setTiktokVideoFile(null);
    setIsUploadingTikTokVideo(false);
    setTiktokSourceType('video-url');
    // Reset TikTok Direct Post API states
    setTiktokCreatorInfo(null);
    setTiktokCreatorInfoLoading(false);
    setTiktokCreatorInfoError('');
    setTiktokCreatorInfoStatus('');
    setTiktokReconnectRequired(false);
    setTiktokPrivacyLevel('');
    setTiktokAllowComment(false);
    setTiktokAllowDuet(false);
    setTiktokAllowStitch(false);
    setTiktokDisclosureEnabled(false);
    setTiktokYourBrand(false);
    setTiktokBrandedContent(false);
    setTiktokMusicUsageConfirmed(false);
    setTiktokIsAigc(false);
    setTiktokVideoDurationSec(null);
    setUrlImg(null);
    setRefresh(false);
    setTagsig('');
    setImgurlig('');
    setBestTime(null);
    setQuery('');
    setValue(0);
    setCreateFlyerMode(false);
    setPostErrorMessage('We have trouble adding the publication');

    // Reset AI Video Generation states
    setAiVideoMode(false);
    setAiImageUrl('');
    setAiScript('');
    setAiCta('Visit Us Today!');
    setAiPhone('');
    setAiWebsite('');
    setAiUseLogo(false);
    setAiLogoUrl('');
    setAiVideoGenerationJobId(null);
    setAiVideoGenerationStatus(null);
    setAiVideoGeneratedUrl(null);
    setIsGeneratingAiVideo(false);
    setAiGenerationStartAtMs(null);
    setAiLastRealDurationSec(null);
    setAiFakeDurationSec(AI_VIDEO_DEFAULT_DURATION_SEC + 20);
    setAiCountdownSec(AI_VIDEO_DEFAULT_DURATION_SEC + 20);
    setAiElapsedSec(0);
    setAiVoiceId('female');
    setIsGeneratingAiScript(false);

    if (tiktokVideoInputRef.current) {
      tiktokVideoInputRef.current.value = '';
    }
  };

  const handleCancel = () => {
    props.onClose && props.onClose();
    resetComposerState();
  };

  const handlePostTypeChange = (value) => {
    if (!hasProPlan || !value) {
      return;
    }

    setPostType(value);
    setIsOffer(value === 'offer');

    if (value !== 'tiktok') {
      setTiktokSourceType('video-url');
      setTiktokVideoFile(null);
      setTiktokVideoPreviewUrl('');

      if (tiktokVideoInputRef.current) {
        tiktokVideoInputRef.current.value = '';
      }
    }
  };

  const handleOfferExit = () => {
    setIsOffer(false);
    setPostType('social');
    setIsOriginalVisible(true);
  };

  const handlePublishAction = () => {
    if (postType === 'offer' && offerModalRef.current) {
      offerModalRef.current.handlePost();
      return;
    }

    saveCurrent();
  };

  const clearAiGenerationIntervals = () => {
    if (aiPollIntervalRef.current) {
      clearInterval(aiPollIntervalRef.current);
      aiPollIntervalRef.current = null;
    }
    if (aiFakeTimerIntervalRef.current) {
      clearInterval(aiFakeTimerIntervalRef.current);
      aiFakeTimerIntervalRef.current = null;
    }
  };

  const isActiveAiGeneration = (generationToken) =>
    activeAiGenerationTokenRef.current === generationToken;

  const finalizeAiGeneration = (generationToken, isSuccess = false) => {
    if (!isActiveAiGeneration(generationToken)) return;

    clearAiGenerationIntervals();

    if (isSuccess && aiGenerationStartAtMs) {
      const measuredDurationSec = Math.max(
        1,
        Math.round((Date.now() - aiGenerationStartAtMs) / 1000)
      );
      setAiLastRealDurationSec(measuredDurationSec);
    }

    setIsGeneratingAiVideo(false);
    setAiGenerationStartAtMs(null);
    activeAiGenerationTokenRef.current = null;
  };

  const formatDurationLabel = (seconds) => {
    const safeSeconds = Math.max(0, Number(seconds) || 0);
    const mins = Math.floor(safeSeconds / 60)
      .toString()
      .padStart(2, '0');
    const secs = Math.floor(safeSeconds % 60)
      .toString()
      .padStart(2, '0');
    return `${mins}:${secs}`;
  };

  const startAiFakeTimer = (
    generationToken,
    startAtMs,
    fakeDurationSeconds
  ) => {
    if (!isActiveAiGeneration(generationToken)) return;

    if (aiFakeTimerIntervalRef.current) {
      clearInterval(aiFakeTimerIntervalRef.current);
    }

    setAiElapsedSec(0);
    setAiCountdownSec(fakeDurationSeconds);

    aiFakeTimerIntervalRef.current = setInterval(() => {
      if (!isActiveAiGeneration(generationToken)) {
        clearAiGenerationIntervals();
        return;
      }

      const elapsedSeconds = Math.max(
        0,
        Math.floor((Date.now() - startAtMs) / 1000)
      );
      const remainingSeconds = Math.max(
        fakeDurationSeconds - elapsedSeconds,
        0
      );

      setAiElapsedSec(elapsedSeconds);
      setAiCountdownSec(remainingSeconds);
    }, 1000);
  };

  const getImageDimensions = (imageSrc) => {
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.crossOrigin = 'anonymous';
      image.onload = () => {
        resolve({
          width: image.naturalWidth || image.width,
          height: image.naturalHeight || image.height
        });
      };
      image.onerror = (error) => reject(error);
      image.src = imageSrc;
    });
  };

  const ingestExternalImageForAiCrop = async (imageUrl) => {
    if (!/^https?:\/\//i.test(imageUrl)) {
      return imageUrl;
    }

    const canAppendCacheBuster =
      !/X-Amz-Signature|X-Amz-Credential|Signature=/i.test(imageUrl);
    const sourceUrl = canAppendCacheBuster
      ? `${imageUrl}${imageUrl.includes('?') ? '&' : '?'}cb=${Date.now()}`
      : imageUrl;

    const response = await axios.post(API_ASSETS, {
      clientId: props.currentClientId,
      urlFile: sourceUrl
    });

    const uploadedLocation = response?.data?.Location;
    if (!uploadedLocation) {
      throw new Error('Could not prepare image for cropping.');
    }

    const dotIndex = uploadedLocation.lastIndexOf('.');
    if (dotIndex === -1) {
      return uploadedLocation;
    }

    return `${uploadedLocation.substring(0, dotIndex)}.jpeg`;
  };

  const getCenteredNineBySixteenCropUrl = async (imageUrl) => {
    const { width, height } = await getImageDimensions(imageUrl);
    const targetAspect = 9 / 16;
    let cropWidth = width;
    let cropHeight = height;

    if (width / height > targetAspect) {
      cropWidth = Math.floor(height * targetAspect);
    } else {
      cropHeight = Math.floor(width / targetAspect);
    }

    const pixelCrop = {
      x: Math.floor((width - cropWidth) / 2),
      y: Math.floor((height - cropHeight) / 2),
      width: Math.max(1, cropWidth),
      height: Math.max(1, cropHeight)
    };

    return await getCroppedImg(imageUrl, pixelCrop, 0);
  };

  const uploadAiCroppedImageToS3 = async (croppedImageBlobUrl) => {
    const croppedImageResponse = await fetch(croppedImageBlobUrl);
    if (!croppedImageResponse.ok) {
      throw new Error('Could not read the cropped image.');
    }

    const croppedImageBlob = await croppedImageResponse.blob();
    const croppedImageFile = new File(
      [croppedImageBlob],
      `ai-video-source-${uuidv4()}.jpg`,
      {
        type: 'image/jpeg'
      }
    );

    const presignedResponse = await fetch(S3_GET_PRESIGNED_URLS, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        bucketName: 'adlersuite-assets-prod',
        files: [
          {
            fileName: croppedImageFile.name,
            fileType: croppedImageFile.type
          }
        ],
        prefix: 'videos'
      })
    });

    if (!presignedResponse.ok) {
      throw new Error('Failed to get upload URL for cropped image.');
    }

    const presignedPayload = await presignedResponse.json();
    const presignedUrl = presignedPayload?.urls?.[0]?.url;

    if (!presignedUrl) {
      throw new Error('No upload URL returned for cropped image.');
    }

    const uploadResponse = await fetch(presignedUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': croppedImageFile.type
      },
      body: croppedImageFile
    });

    if (!uploadResponse.ok) {
      throw new Error('Failed to upload cropped image to S3.');
    }

    const fileKey = presignedPayload?.urls?.[0]?.key;
    const publicUrl = fileKey
      ? `https://media.adlerlocal.com/${fileKey}`
      : null;

    if (!publicUrl) {
      throw new Error('Could not construct URL for cropped image.');
    }

    return publicUrl;
  };

  const handleTikTokSourceTypeChange = (value) => {
    if (aiGenerationLockActive) return;
    setTiktokSourceType(value);
  };

  const suggestAiScript = async () => {
    if (!aiImageUrl?.trim() || isGeneratingAiScript) return;
    setIsGeneratingAiScript(true);
    try {
      const response = await fetch(
        'https://apiagents.replit.app/api/ai-video-replicate/suggest-script',
        {
          method: 'POST',
          headers: {
            'X-Api-Key':
              AI_VIDEO_GENERATION_API_KEY ||
              'fmk_be8d48651db27c9ede27562455bef3c70efa4891',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            imageUrl: aiImageUrl.trim(),
            textOverlay: aiCta || '',
            phone: aiPhone || '',
            website: aiWebsite || ''
          })
        }
      );
      if (!response.ok) {
        throw new Error('Failed to generate script');
      }
      const data = await response.json();
      if (data?.script) {
        setAiScript(String(data.script).slice(0, AI_VIDEO_SCRIPT_MAX_CHARS));
      }
    } catch (error) {
      setValidationErrorMessage('Error generating script: ' + error.message);
      setMessageValidationErrorModalOpen(true);
    } finally {
      setIsGeneratingAiScript(false);
    }
  };

  const generateTiktokCaptionWithAi = async () => {
    // Only proceed if script exists and is not already generating
    if (!aiScript?.trim() || isGeneratingTiktokCaption) return;

    setIsGeneratingTiktokCaption(true);
    setTiktokCaptionGenerationError('');

    try {
      // Replace with your API Gateway endpoint URL
      // For AWS API Gateway: https://your-api-id.execute-api.region.amazonaws.com/stage/generate-tiktok-caption
      const endpointUrl =
        process.env.REACT_APP_TIKTOK_CAPTION_ENDPOINT ||
        'https://your-api-gateway-url/generate-tiktok-caption';

      if (endpointUrl.includes('your-api-gateway-url')) {
        throw new Error(
          'Missing REACT_APP_TIKTOK_CAPTION_ENDPOINT configuration.'
        );
      }

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 20000);

      const response = await fetch(endpointUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        signal: controller.signal,
        body: JSON.stringify({
          script: aiScript.trim()
        })
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData?.error || `Failed to generate caption (${response.status})`
        );
      }

      const data = await response.json();

      if (data?.success && data?.caption) {
        // Auto-fill caption field, truncate to 2200 char limit
        setTiktokCaption(String(data.caption).slice(0, 2200));
      } else if (data?.caption) {
        // Fallback in case caption is directly returned
        setTiktokCaption(String(data.caption).slice(0, 2200));
      } else {
        throw new Error('No caption returned from AI');
      }
    } catch (error) {
      console.error('TikTok caption generation error:', error);
      const isTimeoutError =
        error?.name === 'AbortError' ||
        /aborted|timeout/i.test(error?.message || '');
      setTiktokCaptionGenerationError(
        error.message || 'Failed to generate caption'
      );
      // Show error modal
      setValidationErrorMessage(
        'Error generating TikTok caption: ' +
          (isTimeoutError
            ? 'Request timed out. Please try again.'
            : error.message || 'Unknown error')
      );
      setMessageValidationErrorModalOpen(true);
    } finally {
      setIsGeneratingTiktokCaption(false);
    }
  };

  const handleTikTokVideoSelection = (event) => {
    if (aiGenerationLockActive) return;
    const selectedFile = event.target.files?.[0];

    if (!selectedFile) {
      return;
    }

    if (!selectedFile.type?.startsWith('video/')) {
      setValidationErrorMessage('Please select a valid video file.');
      setMessageValidationErrorModalOpen(true);
      return;
    }

    if (tiktokVideoPreviewUrl && tiktokVideoPreviewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(tiktokVideoPreviewUrl);
    }

    const blobUrl = URL.createObjectURL(selectedFile);
    setTiktokVideoFile(selectedFile);
    setTiktokVideoUrl('');
    setTiktokVideoPreviewUrl(blobUrl);

    // Capture video duration for validation
    const videoEl = document.createElement('video');
    videoEl.preload = 'metadata';
    videoEl.onloadedmetadata = () => {
      setTiktokVideoDurationSec(Math.ceil(videoEl.duration));
      URL.revokeObjectURL(videoEl.src);
    };
    videoEl.src = blobUrl;
  };

  const uploadTikTokVideoToS3 = async () => {
    if (!tiktokVideoFile) {
      throw new Error('Please select a video file to upload.');
    }

    if (!tiktokVideoFile.type?.startsWith('video/')) {
      throw new Error('Selected file is not a valid video.');
    }

    const MAX_VIDEO_SIZE_BYTES = 250 * 1024 * 1024;
    if (tiktokVideoFile.size > MAX_VIDEO_SIZE_BYTES) {
      throw new Error('Video exceeds 250MB. Please select a smaller file.');
    }

    setIsUploadingTikTokVideo(true);

    try {
      // Get presigned URL from S3 endpoint (works for both images and videos)
      // Using the same format as images, but with prefix: 'videos' for proper folder organization
      const presignedResponse = await fetch(S3_GET_PRESIGNED_URLS, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          bucketName: 'adlersuite-assets-prod',
          files: [
            {
              fileName: tiktokVideoFile.name,
              fileType: tiktokVideoFile.type
            }
          ],
          prefix: 'videos'
        })
      });

      if (!presignedResponse.ok) {
        throw new Error('Failed to get a presigned URL for the video.');
      }

      const presignedPayload = await presignedResponse.json();
      const presignedUrl = presignedPayload?.urls?.[0]?.url;

      if (!presignedUrl) {
        throw new Error('No upload URL was returned by the upload service.');
      }

      // Upload the video file to S3
      const uploadResponse = await fetch(presignedUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': tiktokVideoFile.type
        },
        body: tiktokVideoFile
      });

      if (!uploadResponse.ok) {
        throw new Error('Video upload to S3 failed.');
      }

      // Construct the public URL using CloudFront for TikTok (authorized URL only)
      const fileKey = presignedPayload?.urls?.[0]?.key;
      const publicUrl = fileKey
        ? `https://media.adlerlocal.com/${fileKey}`
        : null;

      if (!publicUrl) {
        throw new Error(
          'Could not construct public URL for the uploaded video.'
        );
      }

      setTiktokVideoUrl(publicUrl);
      setTiktokVideoPreviewUrl(publicUrl);

      return publicUrl;
    } finally {
      setIsUploadingTikTokVideo(false);
    }
  };

  const initiateAiVideoGeneration = async () => {
    if (!aiImageUrl?.trim()) {
      setValidationErrorMessage(
        'Please provide an image URL for AI video generation.'
      );
      setMessageValidationErrorModalOpen(true);
      return;
    }

    if (isGeneratingAiVideo) {
      return;
    }

    const trimmedAiScript = (aiScript || '').trim();
    if (
      trimmedAiScript.length < AI_VIDEO_SCRIPT_MIN_CHARS ||
      trimmedAiScript.length > AI_VIDEO_SCRIPT_MAX_CHARS
    ) {
      setValidationErrorMessage(
        `Script must be ${AI_VIDEO_SCRIPT_MIN_CHARS} to ${AI_VIDEO_SCRIPT_MAX_CHARS} characters.`
      );
      setMessageValidationErrorModalOpen(true);
      return;
    }

    const generationToken = `${Date.now()}-${Math.random()
      .toString(36)
      .slice(2)}`;
    const generationStartAt = Date.now();
    const baselineDurationSec = Math.max(
      40,
      Math.round((aiLastRealDurationSec || AI_VIDEO_DEFAULT_DURATION_SEC) + 20)
    );

    activeAiGenerationTokenRef.current = generationToken;
    clearAiGenerationIntervals();

    setIsGeneratingAiVideo(true);
    setAiVideoGenerationStatus('preparing');
    setAiVideoGenerationJobId(null);
    setAiVideoGeneratedUrl(null);
    setAiVideoGenerationRequestId('');
    setAiGenerationStartAtMs(generationStartAt);
    setAiFakeDurationSec(baselineDurationSec);
    setAiCountdownSec(baselineDurationSec);
    setAiElapsedSec(0);

    startAiFakeTimer(generationToken, generationStartAt, baselineDurationSec);

    try {
      const sourceImageUrl = aiImageUrl.trim();
      const requestId = uuidv4();
      setAiVideoGenerationRequestId(requestId);
      const preparedImageUrl = await ingestExternalImageForAiCrop(
        sourceImageUrl
      );
      const croppedImageBlobUrl = await getCenteredNineBySixteenCropUrl(
        preparedImageUrl
      );

      if (!isActiveAiGeneration(generationToken)) {
        URL.revokeObjectURL(croppedImageBlobUrl);
        return;
      }

      let uploadedCroppedImageUrl = '';
      try {
        uploadedCroppedImageUrl = await uploadAiCroppedImageToS3(
          croppedImageBlobUrl
        );
      } finally {
        URL.revokeObjectURL(croppedImageBlobUrl);
      }

      if (!isActiveAiGeneration(generationToken)) {
        return;
      }

      setAiVideoGenerationStatus('pending');

      const payload = {
        imageUrl: uploadedCroppedImageUrl,
        script: trimmedAiScript,
        textOverlay: aiCta || 'Visit Us Today!',
        phone: aiPhone || '',
        website: aiWebsite || '',
        clientRequestId: requestId,
        clientRequestedAt: new Date().toISOString(),
        voiceId: aiVoiceId || 'female',
        ...(aiUseLogo && aiLogoUrl ? { logoUrl: aiLogoUrl } : {})
      };

      const generateEndpoint =
        'https://apiagents.replit.app/api/ai-video-replicate/generate';
      const response = await fetch(
        `${generateEndpoint}?rid=${encodeURIComponent(
          requestId
        )}&ts=${Date.now()}`,
        {
          method: 'POST',
          headers: {
            'X-Api-Key':
              AI_VIDEO_GENERATION_API_KEY ||
              'fmk_be8d48651db27c9ede27562455bef3c70efa4891',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        }
      );

      if (!response.ok) {
        throw new Error('Failed to initiate AI video generation');
      }

      const data = await response.json();
      const jobId = data?.jobId;

      if (!isActiveAiGeneration(generationToken)) {
        return;
      }

      if (!jobId) {
        throw new Error('No job ID returned from API');
      }

      setAiVideoGenerationJobId(jobId);
      pollAiVideoStatus(jobId, generationToken);
    } catch (error) {
      if (!isActiveAiGeneration(generationToken)) {
        return;
      }

      setValidationErrorMessage(
        'Error initiating AI video generation: ' + error.message
      );
      setMessageValidationErrorModalOpen(true);
      setAiVideoGenerationStatus(null);
      finalizeAiGeneration(generationToken, false);
    }
  };

  const pollAiVideoStatus = async (jobId, generationToken) => {
    let pollCount = 0;
    const maxPolls = 120; // 10 minutes with 5-second intervals

    if (aiPollIntervalRef.current) {
      clearInterval(aiPollIntervalRef.current);
    }

    aiPollIntervalRef.current = setInterval(async () => {
      if (!isActiveAiGeneration(generationToken)) {
        clearAiGenerationIntervals();
        return;
      }

      pollCount++;

      if (pollCount > maxPolls) {
        clearAiGenerationIntervals();
        setValidationErrorMessage(
          'AI video generation timed out. Please try again.'
        );
        setMessageValidationErrorModalOpen(true);
        setAiVideoGenerationStatus(null);
        finalizeAiGeneration(generationToken, false);
        return;
      }

      try {
        const response = await fetch(
          `https://apiagents.replit.app/api/ai-video-replicate/status/${jobId}?ts=${Date.now()}`,
          {
            headers: {
              'X-Api-Key':
                AI_VIDEO_GENERATION_API_KEY ||
                'fmk_be8d48651db27c9ede27562455bef3c70efa4891'
            }
          }
        );

        if (!response.ok) {
          throw new Error('Failed to check AI video status');
        }

        const data = await response.json();
        const status = data?.status;

        if (!isActiveAiGeneration(generationToken)) {
          return;
        }

        setAiVideoGenerationStatus(status);

        if (status === 'succeeded') {
          clearAiGenerationIntervals();
          const videoUrl = data?.videoUrl;

          if (videoUrl) {
            // If videoUrl is relative, convert to absolute URL
            let fullVideoUrl = videoUrl;
            if (videoUrl.startsWith('/')) {
              fullVideoUrl = 'https://apiagents.replit.app' + videoUrl;
            }
            // Upload the generated video to S3 using CloudFront
            const uploadedVideoUrl = await uploadGeneratedAiVideoToS3(
              fullVideoUrl,
              generationToken
            );

            if (!isActiveAiGeneration(generationToken)) {
              return;
            }

            setAiVideoGeneratedUrl(uploadedVideoUrl);
            setTiktokVideoUrl(uploadedVideoUrl);
            setTiktokVideoPreviewUrl(uploadedVideoUrl);

            setAiVideoGeneratedSuccessModalOpen(true);
            finalizeAiGeneration(generationToken, true);
          } else {
            throw new Error('No video URL in response');
          }
        } else if (status === 'failed') {
          clearAiGenerationIntervals();
          setValidationErrorMessage(
            'AI video generation failed: ' + (data?.error || 'Unknown error')
          );
          setMessageValidationErrorModalOpen(true);
          finalizeAiGeneration(generationToken, false);
        }
      } catch (error) {
        clearAiGenerationIntervals();
        setValidationErrorMessage(
          'Error polling AI video status: ' + error.message
        );
        setMessageValidationErrorModalOpen(true);
        finalizeAiGeneration(generationToken, false);
      }
    }, 5000);
  };

  const uploadGeneratedAiVideoToS3 = async (videoUrl, generationToken) => {
    try {
      if (!isActiveAiGeneration(generationToken)) {
        throw new Error('AI generation was superseded by a newer request.');
      }

      // Fetch the video from the generated URL
      const videoResponse = await fetch(videoUrl);
      if (!videoResponse.ok) {
        throw new Error('Failed to fetch generated video');
      }

      const videoBlob = await videoResponse.blob();
      const uniqueVideoName = `ai-generated-video-${uuidv4()}.mp4`;
      const videoFile = new File([videoBlob], uniqueVideoName, {
        type: 'video/mp4'
      });

      // Get presigned URL
      const presignedResponse = await fetch(S3_GET_PRESIGNED_URLS, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          bucketName: 'adlersuite-assets-prod',
          files: [
            {
              fileName: videoFile.name,
              fileType: videoFile.type
            }
          ],
          prefix: 'videos'
        })
      });

      if (!presignedResponse.ok) {
        throw new Error('Failed to get presigned URL for AI video');
      }

      const presignedPayload = await presignedResponse.json();
      const presignedUrl = presignedPayload?.urls?.[0]?.url;

      if (!presignedUrl) {
        throw new Error('No upload URL returned');
      }

      // Upload to S3
      const uploadResponse = await fetch(presignedUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': 'video/mp4'
        },
        body: videoFile
      });

      if (!uploadResponse.ok) {
        throw new Error('Failed to upload AI video to S3');
      }

      // Construct CloudFront URL
      const fileKey = presignedPayload?.urls?.[0]?.key;
      const publicUrl = fileKey
        ? `https://media.adlerlocal.com/${fileKey}`
        : null;

      if (!publicUrl) {
        throw new Error('Could not construct public URL');
      }

      return publicUrl;
    } catch (error) {
      setValidationErrorMessage('Error uploading AI video: ' + error.message);
      setMessageValidationErrorModalOpen(true);
      finalizeAiGeneration(generationToken, false);
      throw error;
    }
  };

  const getScheduledUnixTime = () => {
    const timeZone = getTime(props.currentClientTimeZone);
    let rest;
    const isValidDate = (d) => d instanceof Date && !isNaN(d.getTime());
    if (isCustomTime && isValidDate(date)) {
      rest = moment.utc(timeZone).unix() - moment.utc(date).unix();
    } else if (!isCustomTime && bestTime !== null && isValidDate(bestTime)) {
      rest = moment.utc(timeZone).unix() - moment.utc(bestTime).unix();
    } else if (isValidDate(date)) {
      rest = moment.utc(timeZone).unix() - moment.utc(date).unix();
    } else {
      rest = -600;
    }
    return (
      (rest > 0 ? -600 : rest * -1) + moment.utc(new Date()).seconds(0).unix()
    );
  };

  const submitTikTokPost = async () => {
    let captionToSubmit = tiktokCaption.trim();
    let videoUrlToSubmit = tiktokVideoUrl.trim();

    const resetSavingFlags = () => {
      setSavingPost(false);
      setSavingDraft(false);
      setSavingTikTokDraft(false);
      setRepairingPost(false);
    };

    if (isTikTokConnectionBlocked) {
      setTiktokCreatorInfoError('Please reconnect TikTok before posting.');
      resetSavingFlags();
      return;
    }

    if (!tiktokCreatorInfo && !savingDraft) {
      setTiktokCreatorInfoError(
        'TikTok account information could not be loaded. Please reconnect TikTok or try again.'
      );
      resetSavingFlags();
      return;
    }

    if (!captionToSubmit) {
      setNoMessageErrorModalOpen(true);
      resetSavingFlags();
      return;
    }

    if (captionToSubmit.length > 2200) {
      setValidationErrorMessage(
        'TikTok caption must be 2200 characters or fewer.'
      );
      setMessageValidationErrorModalOpen(true);
      resetSavingFlags();
      return;
    }

    if (captionToSubmit.includes('~')) {
      const checkMessage = checkMessageAndReplaceTags(captionToSubmit);

      if (checkMessage.status) {
        setValidationErrorMessage(checkMessage.message);
        setMessageValidationErrorModalOpen(true);
        resetSavingFlags();
        return;
      }

      captionToSubmit = checkMessage.message;
      setTiktokCaption(checkMessage.message);
    }

    // Direct Post validations (not applicable when saving as internal draft)
    if (!savingDraft) {
      if (!tiktokCreatorInfo || tiktokCreatorInfoError) {
        setValidationErrorMessage(
          tiktokCreatorInfoError ||
            'TikTok account info could not be loaded. Please try again.'
        );
        setMessageValidationErrorModalOpen(true);
        resetSavingFlags();
        return;
      }

      if (!tiktokPrivacyLevel) {
        setValidationErrorMessage('Please select who can view this video.');
        setMessageValidationErrorModalOpen(true);
        resetSavingFlags();
        return;
      }

      if (!tiktokMusicUsageConfirmed) {
        setValidationErrorMessage(
          tiktokBrandedContent
            ? "Please confirm TikTok's Branded Content Policy and Music Usage Confirmation."
            : "Please confirm TikTok's Music Usage Confirmation."
        );
        setMessageValidationErrorModalOpen(true);
        resetSavingFlags();
        return;
      }

      if (
        tiktokDisclosureEnabled &&
        !tiktokYourBrand &&
        !tiktokBrandedContent
      ) {
        setValidationErrorMessage(
          'Please select at least one disclosure option: "Your brand" or "Branded content".'
        );
        setMessageValidationErrorModalOpen(true);
        resetSavingFlags();
        return;
      }

      if (tiktokBrandedContent && tiktokPrivacyLevel === 'SELF_ONLY') {
        setValidationErrorMessage(
          'Branded content visibility cannot be set to private.'
        );
        setMessageValidationErrorModalOpen(true);
        resetSavingFlags();
        return;
      }

      if (
        tiktokVideoDurationSec !== null &&
        tiktokCreatorInfo?.maxVideoPostDurationSec &&
        tiktokVideoDurationSec > tiktokCreatorInfo.maxVideoPostDurationSec
      ) {
        setValidationErrorMessage(
          `Video duration (${tiktokVideoDurationSec}s) exceeds the maximum allowed for this account (${tiktokCreatorInfo.maxVideoPostDurationSec}s).`
        );
        setMessageValidationErrorModalOpen(true);
        resetSavingFlags();
        return;
      }
    }

    try {
      if (tiktokSourceType === 'upload-video') {
        if (!videoUrlToSubmit) {
          videoUrlToSubmit = await uploadTikTokVideoToS3();
        }
      } else if (!videoUrlToSubmit) {
        setValidationErrorMessage('Please add a video URL.');
        setMessageValidationErrorModalOpen(true);
        resetSavingFlags();
        return;
      }

      if (!savingDraft && !/^https?:\/\/.+/.test(videoUrlToSubmit)) {
        setValidationErrorMessage('Video URL must be a public http/https URL.');
        setMessageValidationErrorModalOpen(true);
        resetSavingFlags();
        return;
      }

      const scheduleUnix = getScheduledUnixTime();

      const postBody = {
        clientId: props.currentClientId,
        message: captionToSubmit,
        images: [{ text: '', url: '' }],
        schedule: scheduleUnix,
        createdAt: moment.utc().unix(),
        socialNetworks: {
          facebook: false,
          twitter: false,
          googleMyBusiness: false,
          instagram: false,
          tiktok: true,
          tiktokPosted: false
        },
        isDraft: savingDraft,
        recurrentDates: recurrentDates,
        posted: false,
        isScheduled: false,
        createdBy: props.currentCustomerName,
        hashtagsig: '',
        isPromotional: false,
        isOffer: false,
        offer: null,
        tiktok: {
          videoUrl: videoUrlToSubmit,
          asDraft: savingTikTokDraft,
          privacyLevel: tiktokPrivacyLevel,
          allowComment: tiktokAllowComment,
          allowDuet: tiktokAllowDuet,
          allowStitch: tiktokAllowStitch,
          commercialContentDisclosure: {
            enabled: tiktokDisclosureEnabled,
            yourBrand: tiktokYourBrand,
            brandedContent: tiktokBrandedContent
          },
          musicUsageConfirmed: tiktokMusicUsageConfirmed,
          isAigc: tiktokIsAigc,
          publishId: null,
          status: 'PENDING',
          failReason: null
        }
      };

      if (props.post?.id) {
        postBody.postId = props.post.id;
      }

      setPostErrorMessage('We have trouble adding the publication');

      const tikTokPostUrl = props.post?.id ? API_POSTS_EDIT : API_POSTS;
      await axios.post(tikTokPostUrl, JSON.stringify(postBody));

      resetComposerState();
      history.push('/calendar', {
        showSuccessModal: true,
        postDate: postBody.schedule
      });
    } catch (error) {
      console.error('TikTok post error:', error);
      const sourceErrorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error?.message ||
        error?.message;

      setPostErrorMessage(
        error?.response?.data?.message ||
          error?.response?.data?.error?.message ||
          error?.message ||
          'Internal error posting to TikTok'
      );

      if (sourceErrorMessage) {
        setValidationErrorMessage(sourceErrorMessage);
      }

      setPostErrorModalOpen(true);
      resetSavingFlags();
    }
  };

  const addPost = async (post) => {
    if (post['images']) {
      let dimensions = await getSize(post['images'][0]['url']);

      if (dimensions.height < 250 || dimensions.width < 250) {
        setImageDimensions(dimensions);
        setImageTooSmallModalOpen(true);
        setSavingPost(false);
        setSavingDraft(false);
        setRepairingPost(false);
        return;
      }
    }

    let clientId =
      post['client'] !== undefined
        ? post['client']['id']
        : props['currentClientId'];

    if (clientId === undefined) {
      return;
    }

    function setGooglePostType(post, type) {
      post[type] = true;
      post['facebook'] = false;
      post['twitter'] = false;
      post['instagram'] = false;
      post['googleMyBusiness'] = true;
    }

    if (isPromotional) {
      setGooglePostType(post, 'isPromotional');
    }

    if (isOffer) {
      setGooglePostType(post, 'isOffer');
    }

    const postBody = {
      message: post['title'],
      images: post['images'],
      schedule: post['start'],
      createdAt: moment.utc().unix(),
      socialNetworks: {
        facebook: post['facebook'] || false,
        twitter: post['twitter'] || false,
        googleMyBusiness: post['googleMyBusiness'] || false,
        instagram: post['instagram'] || false
      },
      isDraft: post['isDraft'],
      recurrentDates: recurrentDates,
      posted: false,
      isScheduled: false,
      createdBy: props.currentCustomerName,
      hashtagsig: post['hashtagsig'],
      isPromotional: post['isPromotional'] || false,
      isOffer: post['isOffer'] || false
    };
    postBody['clientId'] = clientId;
    if (post['id'] !== undefined) {
      postBody['postId'] = post['id'];
    }

    let url = post['id'] !== undefined ? API_POSTS_EDIT : API_POSTS;

    if (repairingPost) {
      url = API_POSTS_REPAIR;
    }

    axios
      .post(url, JSON.stringify(postBody))
      .then(async (response) => {
        if (response) {
          if (selectedImagePK && selectedImageSK && !urlImg) {
            let img_info = {
              ...(selectedImagePK && { PK: selectedImagePK }),
              ...(selectedImageSK && { SK: selectedImageSK }),
              isUsed: true
            };
            await fetch(MANIPULATE_DESCRIPTION, {
              method: 'POST',
              body: JSON.stringify(img_info),
              headers: {
                'Content-Type': 'application/json'
              }
            });
          }
          if (urlImg) {
            let img_info = {
              userid: props.currentClientId,
              link: urlImg,
              des: des,
              isUsed: true
            };

            await fetch(MANIPULATE_DESCRIPTION, {
              method: 'POST',
              body: JSON.stringify(img_info),
              headers: {
                'Content-Type': 'application/json'
              }
            });
          }
          if (urlImg) {
            setRefresh(!refresh);
          }
          resetComposerState();

          // Navigate to calendar with success state and the post's scheduled date
          history.push('/calendar', {
            showSuccessModal: true,
            postDate: post['start']
          });
        }
      })
      .catch((error) => {
        if (error) {
          setPostErrorMessage(
            error?.response?.data?.message ||
              error?.message ||
              'We have trouble adding the publication'
          );
          setPostErrorModalOpen(true);
          setSavingPost(false);
          setSavingDraft(false);
          setRepairingPost(false);
        }
        console.error(error);
      });
  };

  const savePost = () => {
    if (!postDescription || postDescription.trim().length < 3) {
      setNoMessageErrorModalOpen(true);
      setSavingPost(false);
      setSavingDraft(false);
      setRepairingPost(false);
      return;
    }

    if (postDescription.includes('~')) {
      let checkMessage = checkMessageAndReplaceTags(postDescription);

      if (checkMessage.status) {
        setValidationErrorMessage(checkMessage.message);
        setMessageValidationErrorModalOpen(true);
        setSavingPost(false);
        setSavingDraft(false);
        setRepairingPost(false);
        return;
      } else {
        setPostDescription(checkMessage.message);
        if (message.current) {
          message.current.value = checkMessage.message;
        }
      }
    }

    if (
      assetUrl.value.length === 0 &&
      (!assetAttached.current || assetAttached.current.files.length === 0) &&
      !savingDraft
    ) {
      setNoImageErrorModalOpen(true);
      setSavingPost(false);
      setSavingDraft(false);
      return;
    }

    allUrls.map((url) => {
      return url.replace('/thumbnails', '');
    });

    if (savingDraft) {
      if (croppedImg) {
        detectPost(3);
      } else if (
        assetUrl.value.length === 0 &&
        (!assetAttached.current || assetAttached.current.files[0] === undefined)
      ) {
        uploadPost('', 0);
      } else if (
        assetAttached.current &&
        assetAttached.current.files[0] !== undefined
      ) {
        detectPost(2);
      } else if (
        assetUrl.value !== '' ||
        (post['images'] &&
          post['images'][0] &&
          assetUrl.value !== post['images'][0]['url'])
      ) {
        detectPost(1);
      }
    } else {
      if (croppedImg) {
        detectPost(3);
      } else if (
        assetAttached.current &&
        assetAttached.current.files[0] !== undefined
      ) {
        detectPost(2);
      } else if (
        assetUrl.value !== '' ||
        (post['images'] &&
          post['images'][0] &&
          assetUrl.value !== post['images'][0]['url'])
      ) {
        detectPost(1);
      }
    }
  };

  const detectPost = async (flag) => {
    let bodyLoadImage = {
      clientId: props.currentClientId
    };
    switch (flag) {
      case 1:
        bodyLoadImage['urlFile'] = assetUrl.value;
        uploadImage(bodyLoadImage, flag);
        break;
      case 2:
        fileToBase64(assetAttached.current, (res) => {
          bodyLoadImage['fileB64'] = res;
          uploadImage(bodyLoadImage, flag);
        });
        break;
      case 3:
        blobToBase64(croppedImg, (res) => {
          bodyLoadImage['fileB64'] = res;
          uploadImage(bodyLoadImage, flag);
        });
        break;
      default:
        break;
    }
  };

  const selectSn = (key, val) => {
    if (post['socialNetworks']) {
      setPost((prevPost) => ({
        ...prevPost,
        socialNetworks: {
          ...prevPost.socialNetworks,
          [key]: !prevPost.socialNetworks[key]
        }
      }));
    }
  };

  const uploadPost = (urlImage, flag) => {
    let post1 = post;
    let timeZone = getTime(props.currentClientTimeZone);
    let rest = null;
    const isValidDate = (d) => d instanceof Date && !isNaN(d.getTime());
    if (isCustomTime && isValidDate(date)) {
      rest = moment.utc(timeZone).unix() - moment.utc(date).unix();
    } else if (!isCustomTime && bestTime !== null && isValidDate(bestTime)) {
      rest = moment.utc(timeZone).unix() - moment.utc(bestTime).unix();
    } else if (isValidDate(date)) {
      rest = moment.utc(timeZone).unix() - moment.utc(date).unix();
    } else {
      // Fallback: schedule 10 minutes from now if no valid date is available
      rest = -600;
    }
    let dateNow =
      (rest > 0 ? -600 : rest * -1) + moment.utc(new Date()).seconds(0).unix();

    post1['title'] = postDescription;
    post1['start'] = dateNow;
    if (urlImage !== '') {
      post1['images'] = [
        { url: urlImage, text: urlImage.split('.com/')[1].split('.')[0] || '' }
      ];

      if (flag === 1) {
        post1['images'][0]['originalURL'] = preview;
      }
    }

    let tagsText = '';
    if (tagsPhrasesRef.current !== null) {
      const phrasesTags = Array.from(tagsPhrasesRef.current.childNodes);

      tagsText = phrasesTags.map((nodes) => {
        return nodes['childNodes'][0].data;
      });

      tagsText = tagsText.join('-n-');
    }

    // ShadCN Checkbox components don't expose .checked via refs — use controlled state
    post1['facebook'] = post.socialNetworks?.facebook || false;
    post1['twitter'] = post.socialNetworks?.twitter || false;
    post1['isDraft'] = savingDraft ? true : false;
    post1['googleMyBusiness'] = post.socialNetworks?.googleMyBusiness || false;
    post1['instagram'] = post.socialNetworks?.instagram || false;
    post1['startEditable'] = true;
    post1['backgroundColor'] = '#039be5';
    post1['borderColor'] = '#0168fa';
    post1['textColor'] = '#fff';
    post1['imgurlig'] = imgurlig;
    post1['hashtagsig'] = tagsText;
    addPost(post1);
  };

  const uploadImage = async (bodyLoadImage, flag) => {
    if (flag === 2) {
      if (!assetAttached.current || !assetAttached.current.files[0]) {
        return;
      }
      let file = assetAttached.current.files[0];

      try {
        const mimeType = '.' + file?.name.split('.').pop() || '.jpg';
        let newFileName = `${uuidv4()}${mimeType}`;
        const fileType = file?.type;

        const presignedResponse = await fetch(S3_GET_PRESIGNED_URLS, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            bucketName: 'adlersuite-assets-prod',
            files: [{ fileName: newFileName, fileType }]
          })
        });

        if (!presignedResponse.ok) {
          throw new Error('Failed to get presigned URL');
        }

        const { urls } = await presignedResponse.json();
        const presignedUrl = urls?.[0]?.url;

        if (!presignedUrl) {
          throw new Error('No presigned URL returned');
        }

        const uploadResult = await fetch(presignedUrl, {
          method: 'PUT',
          headers: {
            'Content-Type': fileType
          },
          body: file
        });

        if (!uploadResult.ok) {
          throw new Error('File upload to S3 failed');
        }

        if (!uploadResult.ok) {
          setInvalidUrlErrorModalOpen(true);
          assetUrl.value = '';
          setSavingPost(false);
          return;
        }
        const uploadedFileUrl = `https://${'adlersuite-assets-prod'}.s3.amazonaws.com/${newFileName}`;
        setAssetUrl({ value: uploadedFileUrl });
        uploadPost(uploadedFileUrl, flag);
      } catch (error) {
        console.error('Upload failed:', error);
      }
    } else {
      axios
        .post(API_ASSETS, bodyLoadImage)
        .then((response) => {
          if (response['data']['Location']) {
            let res =
              response['data']['Location'].substring(
                0,
                response['data']['Location'].lastIndexOf('.')
              ) + '.jpeg';
            setAssetUrl({ value: res });
            uploadPost(res, flag);
          }
        })
        .catch((error) => {
          setInvalidUrlErrorModalOpen(true);
          setAssetUrl({ value: '' });
          setSavingPost(false);
        });
    }
  };

  const onChange = (date) => {
    if (date instanceof Date) {
      setDate(date);
    }
  };

  const handleRecurrentCheck = () => {
    setShowRecurrent(!showRecurrent);

    setRecurrentDates(date);
    setChecked(!checked);
  };

  const handleCustomTime = () => {
    setIsCustomTime(!isCustomTime);
    if (!isCustomTime) {
      let temp_date = cloneDeep(date) || getTime(props.currentClientTimeZone);

      // Si no hay fecha seleccionada, inicializar con la fecha/hora actual del cliente
      if (!date) {
        setDate(temp_date);
      }

      const dayName = days[temp_date.getDay()];
      const bestHour = parseInt(props.currentClientBestTimes?.[dayName]);
      if (!isNaN(bestHour)) {
        temp_date.setHours(bestHour, Math.floor(Math.random() * 59));
      }
      setBestTime(new Date(temp_date));
    } else {
      if (!props.isCalendar) {
        setDate(getTime(props.currentClientTimeZone));
      } else {
        if (props.post && props.post.status) {
        } else {
        }
      }
    }
  };

  const setAppImage = (event) => {
    if (!hasProPlan || aiGenerationLockActive) return;
    handlePreview(event);
    setHasSelectedImage(true);
  };

  const checkImg = (param) => {
    if (!hasProPlan || aiGenerationLockActive) return;
    let urlOriginal = param[1];
    let thumbUrl = urlOriginal.split('/');

    urlOriginal = `${thumbUrl[0]}//${thumbUrl[2]}/${thumbUrl[3]}/${thumbUrl[4]}/thumbnails/${thumbUrl[5]}`;
    setAllUrls(param[0]);
    setUrlImg(urlOriginal);
    setDes(param[2]);
  };

  const handlePreview = (event) => {
    if (!hasProPlan || aiGenerationLockActive) return;
    setAssetUrl({ value: event.target.value });
    event.target.files
      ? setPreview(URL.createObjectURL(event.target.files[0]))
      : setPreview(event.target.value);
  };

  const handleSelectImage = (param) => {
    if (!hasProPlan || aiGenerationLockActive) return;
    setHasSelectedImage(true);
    if (param && param.pk && param.sk) {
      setSelectedImagePK(param.pk);
      setSelectedImageSK(param.sk);
      setAssetUrl({ value: param.sk });
      setPreview(param.sk);
      // Auto-fill AI Image URL if in AI video mode
      if (aiVideoMode) {
        setAiImageUrl(param.sk);
      }
    } else if (Array.isArray(param) && param.length === 3) {
      checkImg(param);
      setSelectedImagePK(null);
      setSelectedImageSK(null);
      // Auto-fill AI Image URL if in AI video mode
      if (aiVideoMode && param[1]) {
        setAiImageUrl(param[1]);
      }
    } else {
      setAppImage(param);
      setSelectedImagePK(null);
      setSelectedImageSK(null);
      // Auto-fill AI Image URL if in AI video mode and param has value
      if (aiVideoMode && param?.target?.value) {
        setAiImageUrl(param.target.value);
      }
    }
  };

  const getSize = (imageUrl) => {
    return new Promise((resolve, reject) => {
      const img = new window.Image();
      img.onload = () =>
        resolve({ width: img.naturalWidth, height: img.naturalHeight });
      img.onerror = (err) => reject(err);
      img.crossOrigin = 'anonymous';
      img.src = imageUrl;
    });
  };

  const generateTextFromImage = async () => {
    if (!preview && !croppedImg) {
      setNoImageSelectedErrorModalOpen(true);
      return;
    }

    setIsGeneratingText(true);

    try {
      const imageUrl = croppedImg || preview;

      let selectedDate = null;
      if (isCustomTime && date) {
        selectedDate = date;
      } else if (bestTime) {
        selectedDate = bestTime;
      } else if (date) {
        selectedDate = date;
      } else {
        // Use current time in client's timezone as fallback
        selectedDate = getTime(props.currentClientTimeZone);
      }

      let formattedDate = null;
      if (selectedDate) {
        const day = String(selectedDate.getDate()).padStart(2, '0');
        const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
        const year = selectedDate.getFullYear();
        formattedDate = `${day}/${month}/${year}`;
      }

      // Primero obtener la descripción de la imagen si existe
      let imageDescripcion = '';
      try {
        const descriptionConfig = {
          method: 'post',
          url: MANIPULATE_DESCRIPTION,
          data: {
            userid: props.currentClientId,
            get: true,
            to_suite: true
          }
        };

        const descriptionResponse = await axios(descriptionConfig);
        if (descriptionResponse.data?.images) {
          const imageDescriptions = descriptionResponse.data.images;

          // Buscar la descripción de la imagen actual
          // Probar tanto con la URL original como con la URL de thumbnail
          if (typeof imageDescriptions[imageUrl] === 'object') {
            imageDescripcion = imageDescriptions[imageUrl]?.description || '';
          } else if (typeof imageDescriptions[imageUrl] === 'string') {
            imageDescripcion = imageDescriptions[imageUrl] || '';
          } else {
            // Intentar con la URL de thumbnail si la URL original no tiene descripción
            const thumbnailUrl = imageUrl.includes('/thumbnails/')
              ? imageUrl
              : imageUrl.replace(/\/([^/]+)$/, '/thumbnails/$1');

            if (typeof imageDescriptions[thumbnailUrl] === 'object') {
              imageDescripcion =
                imageDescriptions[thumbnailUrl]?.description || '';
            } else if (typeof imageDescriptions[thumbnailUrl] === 'string') {
              imageDescripcion = imageDescriptions[thumbnailUrl] || '';
            }
          }
        }
      } catch (descError) {
        console.error('Error fetching image description:', descError);
        // Continuar sin descripción si hay error
      }

      const response = await fetch(GENERATE_TEXT_FROM_IMAGE, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          imageUrl: imageUrl,
          clientId: props.currentClientId,
          scheduledDate: formattedDate,
          imageDescripcion: imageDescripcion
        })
      });

      if (!response.ok) {
        throw new Error('Error generating text');
      }

      const data = await response.json();

      if (data.socialMediaText) {
        setPostDescription(data.socialMediaText);
        if (message.current) {
          message.current.value = data.socialMediaText;
        }

        setTextGeneratedSuccessModalOpen(true);
      }
    } catch (error) {
      console.error('Error generating text:', error);
      setTextGenerationErrorModalOpen(true);
    } finally {
      setIsGeneratingText(false);
    }
  };

  const getTime = (currentClientTimeZone) => {
    let date = null;

    switch (currentClientTimeZone) {
      case 'Eastern Daylight Time - (GMT-4)':
        date = moment(new Date())
          .tz('America/New_York')
          .format('YYYY-MM-DDTHH:mm');
        break;
      case 'Central Daylight Time - (GMT-5)':
        date = moment(new Date())
          .tz('America/Chicago')
          .format('YYYY-MM-DDTHH:mm');
        break;
      case 'Mountain Daylight Time - (GMT-6)':
        date = moment(new Date())
          .tz('America/Denver')
          .format('YYYY-MM-DDTHH:mm');
        break;
      case 'Mountain Standard Time - (GMT-7)':
        date = moment(new Date())
          .tz('America/Phoenix')
          .format('YYYY-MM-DDTHH:mm');
        break;
      case 'Pacific Daylight Time - (GMT-7)':
        date = moment(new Date())
          .tz('America/Los_Angeles')
          .format('YYYY-MM-DDTHH:mm');
        break;
      case 'Alaska Daylight Time - (GMT-8)':
        date = moment(new Date())
          .tz('America/Anchorage')
          .format('YYYY-MM-DDTHH:mm');
        break;
      default:
        date = moment(new Date())
          .tz('Pacific/Honolulu')
          .format('YYYY-MM-DDTHH:mm');
        break;
    }

    return new Date(date);
  };

  const renderSocialNetworks = () => {
    const currentClientSocialNetworks =
      props.currentClientSocialNetworks ||
      selectedClient?.clientSocialNetworks ||
      {};
    const clientFlags = selectedClient?.clientFlags || {};

    // Helper: supports both array-of-strings and object-of-booleans formats
    const isFlagSet = (flag) =>
      Array.isArray(clientFlags)
        ? clientFlags.includes(flag)
        : !!clientFlags[flag];

    // Si no hay redes sociales configuradas, mostrar un mensaje
    if (!hasValidData(currentClientSocialNetworks)) {
      return (
        <div className="flex items-center gap-2 text-sm text-muted-foreground bg-muted/50 px-4 py-3 rounded-xl">
          <span>No social networks configured for this client.</span>
        </div>
      );
    }

    // Determine which networks are available (connected AND not blocked by clientFlags)
    const hasFacebook =
      currentClientSocialNetworks.facebook && !isFlagSet('NO_FB');
    const hasInstagram =
      currentClientSocialNetworks.instagram && !isFlagSet('NO_IG');
    const hasGmb =
      currentClientSocialNetworks.googleMyBusiness && !isFlagSet('NO_GMB');
    const hasTwitter = !!currentClientSocialNetworks.twitter;

    // All connected networks are blocked by flags
    if (!hasFacebook && !hasInstagram && !hasGmb && !hasTwitter) {
      return (
        <div className="flex items-center gap-2 text-sm text-muted-foreground bg-muted/50 px-4 py-3 rounded-xl">
          <span>No social networks available for posting at this time.</span>
        </div>
      );
    }

    return (
      <>
        {hasFacebook && (
          <label className="flex items-center gap-2 bg-muted/50 px-4 py-3 rounded-xl cursor-pointer hover:bg-muted transition-colors">
            <Checkbox
              ref={checkFb}
              id="customCheckFb"
              checked={
                post['socialNetworks'] && post['socialNetworks']['facebook']
              }
              disabled={!hasProPlan}
              onCheckedChange={(checked) => selectSn('facebook', checked)}
            />
            <svg
              className="h-5 w-5 text-[#1877F2]"
              viewBox="0 0 24 24"
              fill="currentColor">
              <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
            </svg>
            <span className="text-base">Facebook</span>
          </label>
        )}

        {hasInstagram && (
          <label className="flex items-center gap-2 bg-muted/50 px-4 py-3 rounded-xl cursor-pointer hover:bg-muted transition-colors">
            <Checkbox
              ref={checkIg}
              id="customCheckIg"
              checked={
                post['socialNetworks'] && post['socialNetworks']['instagram']
              }
              disabled={!hasProPlan}
              onCheckedChange={(checked) => selectSn('instagram', checked)}
            />
            <svg
              className="h-5 w-5"
              viewBox="0 0 24 24"
              fill="url(#instagram-gradient-3)">
              <defs>
                <linearGradient
                  id="instagram-gradient-3"
                  x1="0%"
                  y1="100%"
                  x2="100%"
                  y2="0%">
                  <stop offset="0%" stopColor="#FFDC80" />
                  <stop offset="50%" stopColor="#E1306C" />
                  <stop offset="100%" stopColor="#833AB4" />
                </linearGradient>
              </defs>
              <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
            </svg>
            <span className="text-base">Instagram</span>
          </label>
        )}

        {hasGmb && (
          <label className="flex items-center gap-2 bg-muted/50 px-4 py-3 rounded-xl cursor-pointer hover:bg-muted transition-colors">
            <Checkbox
              ref={checkGmb}
              id="customCheckGmb"
              checked={
                post['socialNetworks'] &&
                post['socialNetworks']['googleMyBusiness']
              }
              disabled={!hasProPlan}
              onCheckedChange={(checked) =>
                selectSn('googleMyBusiness', checked)
              }
            />
            <svg className="h-5 w-5" viewBox="0 0 24 24">
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#FBBC05"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            <span className="text-base">Google Business</span>
          </label>
        )}

        {hasTwitter && (
          <label className="flex items-center gap-2 bg-muted/50 px-4 py-3 rounded-xl cursor-pointer hover:bg-muted transition-colors">
            <Checkbox
              ref={checkTw}
              id="customCheckTw"
              checked={
                post['socialNetworks'] && post['socialNetworks']['twitter']
              }
              disabled={!hasProPlan}
              onCheckedChange={(checked) => selectSn('twitter', checked)}
            />
            <span className="text-base">Twitter</span>
          </label>
        )}
      </>
    );
  };

  const onDragStart = (e, key) => {
    e.dataTransfer.setData('key', key);
  };

  const handleClickStart = (e, key) => {
    e.preventDefault();
    setPostDescription(postDescription + key + ' ');
  };

  const onDragOver = (e) => {
    e.preventDefault();
  };

  const onDrop = (e) => {
    setPostDescription(postDescription + e.dataTransfer.getData('key') + ' ');
  };

  const { currentClientTimeZone, currentClientMergeFields } = props;

  let mergeFields = [];

  if (currentClientMergeFields) {
    for (let key in currentClientMergeFields) {
      if (
        currentClientMergeFields[key] &&
        currentClientMergeFields[key].trim().length !== 0
      ) {
        mergeFields.push(
          <Badge
            variant="secondary"
            className="cursor-pointer hover:bg-primary hover:text-primary-foreground transition-colors text-sm py-1.5 px-3 disabled:opacity-50 disabled:cursor-not-allowed"
            key={key}
            onClick={(e) => hasProPlan && handleClickStart(e, key)}
            onDragStart={(e) => hasProPlan && onDragStart(e, key)}
            draggable={hasProPlan}
            style={{
              pointerEvents: hasProPlan ? 'auto' : 'none',
              opacity: hasProPlan ? 1 : 0.5
            }}>
            {key}
          </Badge>
        );
      }
    }
  }

  const getTimeZoneClock = (currentClientTimeZone) => {
    let timeZoneClock = null;
    switch (currentClientTimeZone) {
      case 'Eastern Daylight Time - (GMT-4)':
        timeZoneClock = 'America/New_York';
        break;
      case 'Central Daylight Time - (GMT-5)':
        timeZoneClock = 'America/Chicago';
        break;
      case 'Mountain Daylight Time - (GMT-6)':
        timeZoneClock = 'America/Denver';
        break;
      case 'Mountain Standard Time - (GMT-7)':
        timeZoneClock = 'America/Phoenix';
        break;
      case 'Pacific Daylight Time - (GMT-7)':
        timeZoneClock = 'America/Los_Angeles';
        break;
      case 'Alaska Daylight Time - (GMT-8)':
        timeZoneClock = 'America/Anchorage';
        break;
      default:
        timeZoneClock = 'Pacific/Honolulu';
        break;
    }
    return timeZoneClock;
  };

  const [value, setValue] = useState(0);

  const handleIndexChange = (event, newValue) => {
    setValue(newValue);
  };

  const handleFlyerGenerated = (flyerUrl) => {
    // Set the generated flyer as the only active image, clearing any prior selection
    setPreview(flyerUrl);
    setAssetUrl({ value: flyerUrl });
    setHasSelectedImage(true);
    // Clear leftover state from any previously selected image
    setSelectedImagePK(null);
    setSelectedImageSK(null);
    setUrlImg(null);
    setDes(null);
    setAllUrls([]);
    // Optionally turn off flyer mode after generation
    // setCreateFlyerMode(false);
  };

  if (!props.currentClientId && !selectedClient?.id) {
    return <NewPostSkeleton />;
  }

  return (
    <>
      {/* Backdrop */}
      <main className="p-6 lg:p-10 space-y-8">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              size="icon"
              className="h-12 w-12 rounded-xl"
              onClick={() => history.push('/calendar')}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold">
                New Post, {selectedClient ? `${selectedClient.clientName}` : ''}
              </h1>
              <div className="flex items-center gap-2 text-base text-muted-foreground mt-1">
                <ClockIcon className="h-4 w-4" />
                <span>
                  Eastern Time (GMT-4){' '}
                  <Clock
                    format={'HH:mm:ss'}
                    ticking={true}
                    timezone={getTimeZoneClock(currentClientTimeZone)}
                  />
                </span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Button
              type="button"
              variant="outline"
              size="lg"
              className="rounded-xl h-12 px-6"
              disabled={!hasProPlan}
              onClick={handleCancel}
              ref={modalDismiss}>
              Cancel
            </Button>

            {postType !== 'offer' && (
              <Button
                type="button"
                disabled={!hasProPlan || savingDraft}
                variant="secondary"
                size="lg"
                className="rounded-xl h-12 px-6"
                onClick={() => saveDraft()}>
                <Save className="h-5 w-5 mr-2" />
                {savingDraft ? <>Saving...</> : <>Save Draft</>}
              </Button>
            )}

            {isTikTokPost && (
              <Button
                type="button"
                disabled={
                  !hasProPlan || savingTikTokDraft || tiktokDirectPostDisabled
                }
                variant="outline"
                size="lg"
                className="rounded-xl h-12 px-6"
                onClick={() => setSavingTikTokDraft(true)}>
                <Save className="h-5 w-5 mr-2" />
                {savingTikTokDraft ? <>Saving...</> : <>Post as TikTok Draft</>}
              </Button>
            )}

            {isSocialPost && post.isRepairingPost ? (
              <Button
                type="button"
                disabled={!hasProPlan || repairingPost}
                size="lg"
                className="bg-green-600 hover:bg-green-700 text-white h-14 px-8 text-lg font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all gap-3"
                onClick={() => repairPost()}>
                <Check className="h-5 w-5" />
                {repairingPost ? <>Editing...</> : <>Edit Post</>}
              </Button>
            ) : (
              <Button
                type="button"
                disabled={!hasProPlan || savingPost || tiktokDirectPostDisabled}
                size="lg"
                className="bg-green-600 hover:bg-green-700 text-white h-14 px-8 text-lg font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all gap-3"
                onClick={handlePublishAction}>
                <Check className="h-5 w-5" />
                {savingPost ? <>Publishing...</> : <>Publish</>}
              </Button>
            )}
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 items-stretch">
          <div className="flex flex-col lg:col-span-3">
            {/* Post Type Selection */}

            {/* Edit Image Modal */}
            <EditImgModal
              open={showImgEditModal}
              onOpenChange={setShowImgEditModal}
              img={preview}
              storeOriginal={(img) => setImgurlig(img)}
              onUpdate={(img) => setCroppedImg(img)}
              clientId={props.currentClientId}
            />

            <Card className="shadow-md rounded-2xl border-0 flex-1 flex flex-col">
              <CardHeader className="pb-4 pt-6 px-6 lg:px-8">
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center">
                    <FileText className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <CardTitle className="text-xl lg:text-2xl font-bold">
                      Create Post
                    </CardTitle>
                    <CardDescription className="text-base mt-1">
                      Select the type of content you want to create
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-6 px-6 lg:px-8 pb-8 flex-1 flex flex-col">
                <ToggleGroup
                  type="single"
                  value={postType}
                  onValueChange={handlePostTypeChange}
                  spacing={12}
                  className={`grid ${
                    showTikTokPostTypeOption ? 'grid-cols-3' : 'grid-cols-2'
                  } gap-3`}
                  style={{
                    pointerEvents: hasProPlan ? 'auto' : 'none',
                    opacity: hasProPlan ? 1 : 0.5
                  }}>
                  <ToggleGroupItem
                    value="social"
                    className="flex flex-col items-center gap-2 h-auto py-4 px-3 data-[state=on]:bg-primary data-[state=on]:text-primary-foreground rounded-xl border">
                    <div className="flex items-center gap-1">
                      <svg
                        className="h-4 w-4"
                        viewBox="0 0 24 24"
                        fill="currentColor">
                        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                      </svg>
                      <svg
                        className="h-4 w-4"
                        viewBox="0 0 24 24"
                        fill="currentColor">
                        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
                      </svg>
                      <svg
                        className="h-4 w-4"
                        viewBox="0 0 24 24"
                        fill="currentColor">
                        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                      </svg>
                    </div>
                    <span className="text-sm font-medium">Social Post</span>
                  </ToggleGroupItem>
                  <ToggleGroupItem
                    value="offer"
                    className="flex flex-col items-center gap-2 h-auto py-4 px-3 data-[state=on]:bg-primary data-[state=on]:text-primary-foreground rounded-xl border">
                    <Tag className="!h-6 !w-6" />
                    <span className="text-sm font-medium">Google Offer</span>
                  </ToggleGroupItem>
                  {showTikTokPostTypeOption && (
                    <ToggleGroupItem
                      value="tiktok"
                      disabled={shouldDisableTikTokPosting}
                      className="flex flex-col items-center gap-2 h-auto py-4 px-3 data-[state=on]:bg-primary data-[state=on]:text-primary-foreground rounded-xl border">
                      <svg
                        className="h-6 w-6"
                        viewBox="0 0 24 24"
                        fill="currentColor">
                        <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
                      </svg>
                      <span className="text-sm font-medium">TikTok</span>
                    </ToggleGroupItem>
                  )}
                </ToggleGroup>

                <Separator />

                {postType === 'offer' && (
                  <>
                    <OfferModal
                      ref={offerModalRef}
                      onClose={handleOfferExit}
                      onBack={handleOfferExit}
                      props={props}
                    />
                  </>
                )}
                {isSocialPost && (
                  <SocialPostForm
                    croppedImg={croppedImg}
                    preview={preview}
                    setShowImgEditModal={setShowImgEditModal}
                    postDescription={postDescription}
                    setPostDescription={setPostDescription}
                    assetUrl={assetUrl}
                    handlePreview={handlePreview}
                    assetAttached={assetAttached}
                    hasSelectedImage={hasSelectedImage}
                    isGeneratingText={isGeneratingText}
                    generateTextFromImage={generateTextFromImage}
                    message={message}
                    checkTw={checkTw}
                    resetForm={resetForm}
                    checkFb={checkFb}
                    checkGmb={checkGmb}
                    checkIg={checkIg}
                    showEmojisMenu={showEmojisMenu}
                    setShowEmojisMenu={setShowEmojisMenu}
                    mergeFields={mergeFields}
                    isCustomTime={isCustomTime}
                    handleCustomTime={handleCustomTime}
                    date={date}
                    onChange={onChange}
                    getTime={getTime}
                    currentClientTimeZone={currentClientTimeZone}
                    props={props}
                    days={days}
                    checked={checked}
                    handleRecurrentCheck={handleRecurrentCheck}
                    showRecurrent={showRecurrent}
                    setRecurrentDates={setRecurrentDates}
                    editTimeRecurrentDates={editTimeRecurrentDates}
                    bestTime={bestTime}
                    renderSocialNetworks={renderSocialNetworks}
                    post={post}
                    onDragOver={onDragOver}
                    onDrop={onDrop}
                    hasProPlan={hasProPlan}
                  />
                )}
                {isTikTokPost && (
                  <div className="lg:col-span-3 space-y-6">
                    {/* AI Video Generation Toggle - only visible to superAdmins */}
                    {isSuperAdmin && (
                      <div className="flex items-center justify-between bg-gradient-to-r from-[#25F4EE]/10 to-[#FE2C55]/10 px-4 py-3 rounded-xl border border-[#FE2C55]/20">
                        <div className="flex items-center gap-2">
                          <Sparkles className="h-5 w-5 text-[#FE2C55]" />
                          <Label
                            htmlFor="ai-video-mode"
                            className="text-base font-medium cursor-pointer">
                            Generate AI Video
                          </Label>
                        </div>
                        <Switch
                          id="ai-video-mode"
                          checked={aiVideoMode}
                          disabled={isGeneratingAiVideo}
                          onCheckedChange={setAiVideoMode}
                        />
                      </div>
                    )}

                    {/* AI Video Form (shown when AI mode is on) */}
                    {isSuperAdmin && aiVideoMode && (
                      <div className="space-y-4 bg-muted/30 rounded-xl p-4 border border-muted">
                        <div className="space-y-2">
                          <Label className="text-base font-medium">
                            Image URL
                          </Label>
                          <Input
                            value={aiImageUrl}
                            onChange={(e) => setAiImageUrl(e.target.value)}
                            placeholder="https://example.com/my-image.jpg"
                            className="h-12 text-base"
                            disabled={aiGenerationLockActive}
                          />
                        </div>
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <Label className="text-base font-medium">
                              Script (voice)
                            </Label>
                            <button
                              type="button"
                              onClick={suggestAiScript}
                              disabled={
                                !aiImageUrl?.trim() ||
                                isGeneratingAiScript ||
                                aiGenerationLockActive
                              }
                              className="flex items-center gap-1 text-xs text-violet-600 hover:text-violet-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors font-medium">
                              <Sparkles className="h-3.5 w-3.5" />
                              {isGeneratingAiScript
                                ? 'Generating...'
                                : 'AI Script'}
                            </button>
                          </div>
                          <Textarea
                            value={aiScript}
                            onChange={(e) =>
                              setAiScript(
                                e.target.value.slice(
                                  0,
                                  AI_VIDEO_SCRIPT_MAX_CHARS
                                )
                              )
                            }
                            placeholder="Text that will be spoken in the video..."
                            className="min-h-[100px] resize-none text-base"
                            maxLength={AI_VIDEO_SCRIPT_MAX_CHARS}
                            disabled={aiGenerationLockActive}
                          />
                          <div className="text-xs text-muted-foreground text-right">
                            {aiScriptLength}/{AI_VIDEO_SCRIPT_MAX_CHARS} (min{' '}
                            {AI_VIDEO_SCRIPT_MIN_CHARS})
                          </div>
                          <div className="flex items-center gap-6 pt-1">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="radio"
                                name="aiVoiceId"
                                value="female"
                                checked={aiVoiceId === 'female'}
                                onChange={() => setAiVoiceId('female')}
                                className="accent-violet-600"
                              />
                              <span className="text-sm">Female</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="radio"
                                name="aiVoiceId"
                                value="male"
                                checked={aiVoiceId === 'male'}
                                onChange={() => setAiVoiceId('male')}
                                className="accent-violet-600"
                              />
                              <span className="text-sm">Male</span>
                            </label>
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-base font-medium">
                            Text on screen (CTA)
                          </Label>
                          <Input
                            value={aiCta}
                            onChange={(e) => setAiCta(e.target.value)}
                            placeholder="Visit Us Today!"
                            className="h-12 text-base"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-base font-medium">Phone</Label>
                          <Input
                            value={aiPhone}
                            onChange={(e) => setAiPhone(e.target.value)}
                            placeholder="(555) 222-3333"
                            className="h-12 text-base"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-base font-medium">
                            Website
                          </Label>
                          <Input
                            value={aiWebsite}
                            onChange={(e) => setAiWebsite(e.target.value)}
                            placeholder="www.example.com"
                            className="h-12 text-base"
                          />
                        </div>
                        <div className="flex items-center justify-between bg-gradient-to-r from-blue-500/10 to-purple-500/10 px-4 py-3 rounded-xl border border-blue-500/20">
                          <div className="flex items-center gap-2">
                            <Label
                              htmlFor="ai-use-logo"
                              className="text-base font-medium cursor-pointer">
                              Use Logo
                            </Label>
                          </div>
                          <Switch
                            id="ai-use-logo"
                            checked={aiUseLogo}
                            onCheckedChange={setAiUseLogo}
                          />
                        </div>
                        {aiUseLogo && (
                          <div className="space-y-2">
                            <Label className="text-base font-medium">
                              Logo URL
                            </Label>
                            <Input
                              value={aiLogoUrl}
                              onChange={(e) => setAiLogoUrl(e.target.value)}
                              placeholder="https://example.com/logo.png"
                              className="h-12 text-base"
                            />
                          </div>
                        )}
                        <Button
                          type="button"
                          className="w-full h-12 bg-[#FE2C55] hover:bg-[#FE2C55]/90 text-white gap-2"
                          disabled={
                            isGeneratingAiVideo || !isAiScriptLengthValid
                          }
                          onClick={initiateAiVideoGeneration}>
                          <Video className="h-5 w-5" />
                          {isGeneratingAiVideo
                            ? 'Generating...'
                            : 'Generate AI Video'}
                        </Button>
                        {isGeneratingAiVideo && (
                          <div className="rounded-lg bg-muted/50 p-3 space-y-2">
                            <div className="flex items-center gap-2 justify-center">
                              <div className="animate-spin rounded-full h-4 w-4 border border-primary border-t-transparent"></div>
                              <p className="text-sm text-muted-foreground">
                                Generating video...
                              </p>
                            </div>
                            <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-amber-900">
                              <p className="text-xs font-semibold">
                                Do not reload this page while the AI video is
                                being generated.
                              </p>
                              <p className="text-xs mt-1">
                                Estimated time: 3:00
                              </p>
                              <p className="text-xs">
                                Remaining: {formatDurationLabel(aiCountdownSec)}
                              </p>
                              {aiCountdownSec === 0 && (
                                <p className="text-xs mt-1">
                                  Finalizing generation... this may take a few
                                  extra seconds.
                                </p>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    <Separator />

                    {/* Video Preview */}
                    <div className="border border-border rounded-2xl bg-card p-6">
                      <div className="w-full flex justify-center">
                        <div className="w-full max-w-[320px]">
                          {isGeneratingAiVideo ? (
                            <div className="aspect-[9/16] max-h-[420px] rounded-2xl bg-black flex items-center justify-center">
                              <div className="text-center px-6">
                                <div className="inline-flex items-center justify-center mb-4">
                                  <div className="relative w-16 h-16">
                                    <div className="absolute inset-0 rounded-full border-4 border-[#FE2C55]/20"></div>
                                    <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-[#FE2C55] animate-spin"></div>
                                  </div>
                                </div>
                                <p className="text-sm text-white/80">
                                  Generating video...
                                </p>
                              </div>
                            </div>
                          ) : tiktokVideoPreviewUrl || tiktokVideoUrl ? (
                            <video
                              src={tiktokVideoPreviewUrl || tiktokVideoUrl}
                              controls
                              preload="metadata"
                              className="aspect-[9/16] w-full max-h-[420px] rounded-2xl bg-black object-contain"
                            />
                          ) : (
                            <div className="aspect-[9/16] max-h-[420px] rounded-2xl bg-black flex items-center justify-center">
                              <div className="text-center px-6 text-white">
                                <Video className="h-12 w-12 mx-auto mb-3 text-white/70" />
                                <p className="text-sm text-white/80">
                                  No video preview
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                      {tiktokCaption && (
                        <div className="p-4 border-t border-border">
                          <p className="text-base text-foreground whitespace-pre-wrap">
                            {tiktokCaption}
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Video Caption */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between gap-2">
                        <Label className="text-base font-medium">
                          Video Caption
                        </Label>
                        <button
                          type="button"
                          onClick={generateTiktokCaptionWithAi}
                          disabled={
                            !hasAiScriptForCaption || isGeneratingTiktokCaption
                          }
                          className="flex items-center gap-1 text-xs text-violet-600 hover:text-violet-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors font-medium">
                          <Sparkles className="h-3.5 w-3.5" />
                          {isGeneratingTiktokCaption
                            ? 'Generating...'
                            : 'Generate with AI'}
                        </button>
                      </div>

                      {tiktokCreatorInfoLoading && (
                        <div className="rounded-xl border bg-muted/40 p-4 flex items-center gap-3 text-sm text-muted-foreground">
                          Loading TikTok account...
                        </div>
                      )}
                      {tiktokReconnectRequired && !tiktokCreatorInfoLoading && (
                        <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-4 space-y-3">
                          <div>
                            <p className="font-medium text-destructive">
                              {tiktokCreatorInfoStatus === 'NOT_CONNECTED'
                                ? 'TikTok is not connected'
                                : 'TikTok needs to be reconnected'}
                            </p>
                            <p className="text-sm text-muted-foreground mt-0.5">
                              {tiktokCreatorInfoError ||
                                'Your TikTok connection expired. Please reconnect TikTok before posting.'}
                            </p>
                          </div>
                          <Button
                            type="button"
                            size="sm"
                            onClick={handleReconnectTikTok}>
                            {tiktokCreatorInfoStatus === 'NOT_CONNECTED'
                              ? 'Connect TikTok'
                              : 'Reconnect TikTok'}
                          </Button>
                        </div>
                      )}
                      {tiktokCreatorInfoError &&
                        !tiktokReconnectRequired &&
                        !tiktokCreatorInfoLoading && (
                          <div className="rounded-xl border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
                            {tiktokCreatorInfoError}
                          </div>
                        )}
                      {tiktokCreatorInfo && !tiktokCreatorInfoLoading && (
                        <div className="rounded-xl border bg-muted/40 p-4 flex items-center gap-3">
                          {tiktokCreatorInfo.avatarUrl && (
                            <img
                              src={tiktokCreatorInfo.avatarUrl}
                              alt={
                                tiktokCreatorInfo.nickname || 'TikTok account'
                              }
                              className="h-10 w-10 rounded-full object-cover shrink-0"
                            />
                          )}
                          <div className="min-w-0">
                            <p className="text-sm font-semibold leading-tight truncate">
                              {tiktokCreatorInfo.nickname ||
                                tiktokCreatorInfo.username}
                            </p>
                            {tiktokCreatorInfo.username && (
                              <p className="text-xs text-muted-foreground truncate">
                                @{tiktokCreatorInfo.username}
                              </p>
                            )}
                          </div>
                          <Badge
                            variant="outline"
                            className="ml-auto shrink-0 text-xs">
                            Posting to TikTok as
                          </Badge>
                        </div>
                      )}

                      <Textarea
                        value={tiktokCaption}
                        onChange={(event) =>
                          setTiktokCaption(event.target.value.slice(0, 2200))
                        }
                        className="min-h-[120px] resize-none text-base"
                        placeholder="Write a catchy caption for your TikTok video..."
                        maxLength={2200}
                      />
                      <p className="text-sm text-muted-foreground text-right">
                        {tiktokCaption.length}/2200
                      </p>
                    </div>

                    {/* Video Source - Always visible */}
                    <Separator />
                    <div className="space-y-2">
                      <Label className="text-base font-medium">
                        Video Source
                      </Label>
                      <Tabs
                        value={tiktokSourceType}
                        onValueChange={handleTikTokSourceTypeChange}
                        className="w-full">
                        <TabsList className="grid w-full grid-cols-2">
                          <TabsTrigger
                            value="upload-video"
                            disabled={aiGenerationLockActive}
                            className="flex items-center justify-center gap-2">
                            <Upload className="h-4 w-4" />
                            Upload Video
                          </TabsTrigger>
                          <TabsTrigger
                            value="video-url"
                            disabled={aiGenerationLockActive}
                            className="flex items-center justify-center gap-2">
                            <LinkIcon className="h-4 w-4" />
                            Video URL
                          </TabsTrigger>
                        </TabsList>
                        <TabsContent tabValue="upload-video" className="mt-4">
                          <div className="space-y-3">
                            <input
                              ref={tiktokVideoInputRef}
                              type="file"
                              accept="video/*"
                              className="hidden"
                              onChange={handleTikTokVideoSelection}
                            />
                            <div className="border-2 border-dashed border-muted-foreground/30 rounded-xl p-6 text-center space-y-3">
                              <Video className="h-10 w-10 mx-auto text-muted-foreground" />
                              <p className="text-base font-medium">
                                Select a video file
                              </p>
                              <p className="text-sm text-muted-foreground">
                                MP4/WebM/MOV recommended. Max size: 250MB.
                              </p>
                              <Button
                                type="button"
                                variant="outline"
                                onClick={() =>
                                  tiktokVideoInputRef.current?.click()
                                }
                                disabled={
                                  !hasProPlan ||
                                  isUploadingTikTokVideo ||
                                  aiGenerationLockActive
                                }>
                                <Upload className="h-4 w-4 mr-2" />
                                Choose Video
                              </Button>
                            </div>
                            {tiktokVideoFile && (
                              <div className="rounded-xl bg-muted/50 px-4 py-3 text-sm">
                                <p className="font-medium break-all">
                                  {tiktokVideoFile.name}
                                </p>
                                <p className="text-muted-foreground">
                                  {(
                                    tiktokVideoFile.size /
                                    (1024 * 1024)
                                  ).toFixed(2)}{' '}
                                  MB
                                </p>
                              </div>
                            )}
                            {isUploadingTikTokVideo && (
                              <p className="text-sm text-muted-foreground">
                                Uploading video...
                              </p>
                            )}
                            {tiktokVideoUrl && (
                              <p className="text-sm text-muted-foreground break-all">
                                Uploaded URL: {tiktokVideoUrl}
                              </p>
                            )}
                          </div>
                        </TabsContent>
                        <TabsContent
                          tabValue="video-url"
                          className="mt-4 space-y-3">
                          <Input
                            value={tiktokVideoUrl}
                            onChange={(event) =>
                              setTiktokVideoUrl(event.target.value)
                            }
                            placeholder="https://example.com/video.mp4"
                            className="h-12 text-base"
                            disabled={aiGenerationLockActive}
                          />
                          <p className="text-sm text-muted-foreground">
                            Use a publicly accessible S3 or CDN video URL.{' '}
                            {tiktokVideoUrl && aiVideoGeneratedUrl && (
                              <span className="text-green-600 font-medium">
                                ✓ AI video uploaded
                              </span>
                            )}
                          </p>
                        </TabsContent>
                      </Tabs>
                    </div>

                    <Separator />

                    {/* Schedule Post */}
                    <div
                      className="flex items-center justify-between"
                      style={{
                        pointerEvents: !hasProPlan ? 'none' : 'auto',
                        opacity: !hasProPlan ? 0.5 : 1
                      }}>
                      <div className="flex items-center gap-3">
                        <ClockIcon className="h-5 w-5 text-primary" />
                        <Label className="text-base font-medium">
                          Schedule Post
                        </Label>
                      </div>
                      <Switch
                        checked={isCustomTime}
                        onCheckedChange={() => handleCustomTime()}
                        disabled={!hasProPlan}
                      />
                    </div>
                    {isCustomTime ? (
                      <div className="pl-8 space-y-4">
                        <div className="space-y-2">
                          <Label className="text-sm font-medium">
                            Custom Date
                          </Label>
                          <DateTimePicker
                            onChange={setDate}
                            disableClock
                            value={date}
                            minDate={getTime(props.currentClientTimeZone)}
                            name="tiktokScheduleDate"
                            showTime={{ use12Hours: true }}
                            format="dd/MM/yyyy hh:mm a"
                            clearIcon={null}
                            calendarIcon={null}
                            className="max-w-[220px] h-12"
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="pl-8 space-y-3">
                        <div className="rounded-lg bg-muted/50 p-3">
                          <p className="text-xs text-muted-foreground mb-1">
                            Scheduled for:
                          </p>
                          <p className="text-sm font-medium text-foreground">
                            {bestTime
                              ? bestTime.toLocaleString()
                              : 'Loading...'}
                          </p>
                        </div>
                      </div>
                    )}

                    <Separator />

                    {/* TikTok Post Settings Collapsible */}
                    <Collapsible
                      open={tiktokSettingsOpen}
                      onOpenChange={setTiktokSettingsOpen}>
                      <CollapsibleTrigger className="flex items-center justify-between w-full py-2">
                        <div className="flex items-center gap-3">
                          <svg
                            className="h-5 w-5 text-[#FE2C55]"
                            viewBox="0 0 24 24"
                            fill="currentColor">
                            <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 00-.79-.05A6.34 6.34 0 003.15 15.2a6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.34-6.34V8.75a8.18 8.18 0 004.76 1.52V6.84a4.83 4.83 0 01-1-.15z" />
                          </svg>
                          <Label className="text-base font-medium cursor-pointer">
                            TikTok Post Settings
                          </Label>
                        </div>
                        <ChevronDown
                          className={`h-5 w-5 text-muted-foreground transition-transform ${
                            tiktokSettingsOpen ? 'rotate-180' : ''
                          }`}
                        />
                      </CollapsibleTrigger>
                      <CollapsibleContent className="pt-4 space-y-5">
                        {/* Privacy Level */}
                        <div className="space-y-2">
                          <Label className="text-sm font-medium">
                            Who can view this video?
                          </Label>
                          <Select
                            value={tiktokPrivacyLevel}
                            onValueChange={(val) => setTiktokPrivacyLevel(val)}>
                            <SelectTrigger className="h-11 text-base">
                              <SelectValue placeholder="Select privacy" />
                            </SelectTrigger>
                            <SelectContent>
                              {(
                                tiktokCreatorInfo?.privacyLevelOptions || [
                                  'PUBLIC_TO_EVERYONE',
                                  'MUTUAL_FOLLOW_FRIENDS',
                                  'FOLLOWER_OF_CREATOR',
                                  'SELF_ONLY'
                                ]
                              ).map((opt) => {
                                const labels = {
                                  PUBLIC_TO_EVERYONE: 'Public',
                                  MUTUAL_FOLLOW_FRIENDS: 'Friends',
                                  FOLLOWER_OF_CREATOR: 'Followers',
                                  SELF_ONLY: 'Only me'
                                };
                                const isDisabled =
                                  opt === 'SELF_ONLY' && tiktokBrandedContent;
                                return (
                                  <SelectItem
                                    key={opt}
                                    value={opt}
                                    disabled={isDisabled}>
                                    {labels[opt] || opt}
                                  </SelectItem>
                                );
                              })}
                            </SelectContent>
                          </Select>
                          {tiktokBrandedContent &&
                            tiktokPrivacyLevel === 'SELF_ONLY' && (
                              <p className="text-xs text-destructive">
                                Branded content visibility cannot be set to
                                private.
                              </p>
                            )}
                        </div>

                        {/* Allow Users To */}
                        <div className="space-y-3">
                          <Label className="text-sm font-medium">
                            Allow users to
                          </Label>
                          <div className="flex flex-col gap-2">
                            <div className="flex items-center gap-2">
                              <Checkbox
                                id="tiktok-allow-comment"
                                checked={tiktokAllowComment}
                                disabled={!!tiktokCreatorInfo?.commentDisabled}
                                onCheckedChange={(v) =>
                                  setTiktokAllowComment(!!v)
                                }
                              />
                              <label
                                htmlFor="tiktok-allow-comment"
                                className={`text-sm ${
                                  tiktokCreatorInfo?.commentDisabled
                                    ? 'text-muted-foreground'
                                    : ''
                                }`}>
                                Comment
                              </label>
                            </div>
                            <div className="flex items-center gap-2">
                              <Checkbox
                                id="tiktok-allow-duet"
                                checked={tiktokAllowDuet}
                                disabled={!!tiktokCreatorInfo?.duetDisabled}
                                onCheckedChange={(v) => setTiktokAllowDuet(!!v)}
                              />
                              <label
                                htmlFor="tiktok-allow-duet"
                                className={`text-sm ${
                                  tiktokCreatorInfo?.duetDisabled
                                    ? 'text-muted-foreground'
                                    : ''
                                }`}>
                                Duet
                              </label>
                            </div>
                            <div className="flex items-center gap-2">
                              <Checkbox
                                id="tiktok-allow-stitch"
                                checked={tiktokAllowStitch}
                                disabled={!!tiktokCreatorInfo?.stitchDisabled}
                                onCheckedChange={(v) =>
                                  setTiktokAllowStitch(!!v)
                                }
                              />
                              <label
                                htmlFor="tiktok-allow-stitch"
                                className={`text-sm ${
                                  tiktokCreatorInfo?.stitchDisabled
                                    ? 'text-muted-foreground'
                                    : ''
                                }`}>
                                Stitch
                              </label>
                            </div>
                          </div>
                        </div>

                        {/* Commercial Content Disclosure */}
                        <div className="space-y-3">
                          <div className="flex items-start gap-3">
                            <Switch
                              id="tiktok-disclosure"
                              checked={tiktokDisclosureEnabled}
                              onCheckedChange={(v) => {
                                setTiktokDisclosureEnabled(v);
                                if (!v) {
                                  setTiktokYourBrand(false);
                                  setTiktokBrandedContent(false);
                                }
                              }}
                            />
                            <div>
                              <label
                                htmlFor="tiktok-disclosure"
                                className="text-sm font-medium cursor-pointer">
                                Disclose video content
                              </label>
                              <p className="text-xs text-muted-foreground mt-0.5">
                                Turn on to disclose that this video promotes
                                goods or services in exchange for something of
                                value.
                              </p>
                            </div>
                          </div>
                          {tiktokDisclosureEnabled && (
                            <div className="ml-10 space-y-3 rounded-xl border bg-muted/30 p-4">
                              <div className="flex items-start gap-2">
                                <Checkbox
                                  id="tiktok-your-brand"
                                  checked={tiktokYourBrand}
                                  onCheckedChange={(v) =>
                                    setTiktokYourBrand(!!v)
                                  }
                                />
                                <div>
                                  <label
                                    htmlFor="tiktok-your-brand"
                                    className="text-sm font-medium cursor-pointer">
                                    Your brand
                                  </label>
                                  <p className="text-xs text-muted-foreground mt-0.5">
                                    You are promoting yourself or your own
                                    business. This video will be classified as
                                    Brand Organic.
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-start gap-2">
                                <Checkbox
                                  id="tiktok-branded-content"
                                  checked={tiktokBrandedContent}
                                  onCheckedChange={(v) => {
                                    setTiktokBrandedContent(!!v);
                                    if (
                                      !!v &&
                                      tiktokPrivacyLevel === 'SELF_ONLY'
                                    ) {
                                      setTiktokPrivacyLevel('');
                                    }
                                  }}
                                />
                                <div>
                                  <label
                                    htmlFor="tiktok-branded-content"
                                    className="text-sm font-medium cursor-pointer">
                                    Branded content
                                  </label>
                                  <p className="text-xs text-muted-foreground mt-0.5">
                                    You are promoting another brand or a third
                                    party. This video will be classified as
                                    Branded Content.
                                  </p>
                                </div>
                              </div>
                              {tiktokDisclosureEnabled &&
                                !tiktokYourBrand &&
                                !tiktokBrandedContent && (
                                  <p className="text-xs text-destructive">
                                    Please select at least one disclosure type.
                                  </p>
                                )}
                              {tiktokDisclosureEnabled &&
                                tiktokYourBrand &&
                                !tiktokBrandedContent && (
                                  <p className="text-xs text-muted-foreground">
                                    Your video will be labeled as
                                    &ldquo;Promotional content&rdquo;.
                                  </p>
                                )}
                              {tiktokDisclosureEnabled &&
                                tiktokBrandedContent && (
                                  <p className="text-xs text-muted-foreground">
                                    Your video will be labeled as &ldquo;Paid
                                    partnership&rdquo;.
                                  </p>
                                )}
                            </div>
                          )}
                        </div>

                        {/* AI-generated content */}
                        <div className="flex items-center gap-2">
                          <Checkbox
                            id="tiktok-aigc"
                            checked={tiktokIsAigc || aiVideoMode}
                            onCheckedChange={(v) =>
                              !aiVideoMode && setTiktokIsAigc(!!v)
                            }
                            disabled={aiVideoMode}
                          />
                          <label
                            htmlFor="tiktok-aigc"
                            className={`text-sm ${
                              aiVideoMode ? 'text-muted-foreground' : ''
                            }`}>
                            This video contains AI-generated content
                          </label>
                        </div>

                        <Separator />

                        {/* Music Usage Confirmation */}
                        <div className="space-y-2">
                          <div className="flex items-start gap-2">
                            <Checkbox
                              id="tiktok-music-confirmation"
                              checked={tiktokMusicUsageConfirmed}
                              onCheckedChange={(v) =>
                                setTiktokMusicUsageConfirmed(!!v)
                              }
                            />
                            <label
                              htmlFor="tiktok-music-confirmation"
                              className="text-sm cursor-pointer">
                              {tiktokBrandedContent
                                ? "By posting, you agree to TikTok's Branded Content Policy and Music Usage Confirmation."
                                : "By posting, you agree to TikTok's Music Usage Confirmation."}
                            </label>
                          </div>
                        </div>

                        {/* Processing notice */}
                        <p className="text-xs text-muted-foreground">
                          After publishing, your video may take a few minutes to
                          process and appear on your TikTok profile.
                        </p>
                      </CollapsibleContent>
                    </Collapsible>
                  </div>
                )}

                <div className="flex items-center justify-end gap-3 pt-4 border-t border-border">
                  <Button
                    type="button"
                    variant="outline"
                    size="lg"
                    className="rounded-xl h-12 px-6"
                    disabled={!hasProPlan}
                    onClick={handleCancel}
                    ref={modalDismiss}>
                    Cancel
                  </Button>

                  {postType !== 'offer' && (
                    <Button
                      type="button"
                      disabled={!hasProPlan || savingDraft}
                      variant="secondary"
                      size="lg"
                      className="rounded-xl h-12 px-6"
                      onClick={() => saveDraft()}>
                      <Save className="h-5 w-5 mr-2" />
                      {savingDraft ? <>Saving...</> : <>Save Draft</>}
                    </Button>
                  )}

                  {isTikTokPost && (
                    <Button
                      type="button"
                      disabled={
                        !hasProPlan ||
                        savingTikTokDraft ||
                        tiktokDirectPostDisabled
                      }
                      variant="outline"
                      size="lg"
                      className="rounded-xl h-12 px-6"
                      onClick={() => setSavingTikTokDraft(true)}>
                      <Save className="h-5 w-5 mr-2" />
                      {savingTikTokDraft ? (
                        <>Saving...</>
                      ) : (
                        <>Post as TikTok Draft</>
                      )}
                    </Button>
                  )}

                  {isSocialPost && post.isRepairingPost ? (
                    <Button
                      type="button"
                      disabled={!hasProPlan || repairingPost}
                      size="lg"
                      className="bg-green-600 hover:bg-green-700 text-white h-14 px-8 text-lg font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all gap-3"
                      onClick={() => repairPost()}>
                      <Check className="h-5 w-5" />
                      {repairingPost ? <>Editing...</> : <>Edit Post</>}
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      disabled={
                        !hasProPlan || savingPost || tiktokDirectPostDisabled
                      }
                      size="lg"
                      className="bg-green-600 hover:bg-green-700 text-white h-14 px-8 text-lg font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all gap-3"
                      onClick={handlePublishAction}>
                      <Check className="h-5 w-5" />
                      {savingPost ? <>Publishing...</> : <>Publish</>}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
          {isSocialPost && (
            <div className="lg:col-span-2 flex flex-col gap-6">
              {/* Create Flyer Toggle Card - Controlled by environment variable */}
              {FLYER_FEATURE_ENABLED && (
                <Card className="shadow-md rounded-2xl border-0">
                  <CardContent className="px-6 lg:px-8 py-5">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center">
                          <FileImage className="h-6 w-6 text-primary" />
                        </div>
                        <div>
                          <p className="text-lg font-semibold">Create Flyer</p>
                          <p className="text-sm text-muted-foreground">
                            Generate a custom flyer with AI
                          </p>
                        </div>
                      </div>
                      <Switch
                        checked={createFlyerMode}
                        onCheckedChange={setCreateFlyerMode}
                        disabled={!hasProPlan}
                      />
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Conditionally show Create Flyer or Select Image */}
              {createFlyerMode ? (
                <CreateFlyerCard
                  onFlyerGenerated={handleFlyerGenerated}
                  currentClientId={props.currentClientId}
                  currentClientBrand={selectedClient?.brand}
                  currentClientMergeFields={props.currentClientMergeFields}
                  postsLists={postsListsData}
                  currentClientLogoUrl={selectedClient?.logoUrl || null}
                />
              ) : (
                <Card className="shadow-md rounded-2xl border-0 flex flex-col overflow-auto">
                  <CardHeader className="pb-4 pt-6 px-6 lg:px-8">
                    <div className="flex items-center gap-4">
                      <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center">
                        <Grid3X3 className="h-6 w-6 text-primary" />
                      </div>
                      <div>
                        <CardTitle className="text-xl lg:text-2xl font-bold">
                          Select Image
                        </CardTitle>
                        <CardDescription className="text-base mt-1">
                          Choose from your image galleries
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent
                    className="px-6 lg:px-8 pb-8 flex-1"
                    style={{
                      pointerEvents:
                        hasProPlan && !aiGenerationLockActive ? 'auto' : 'none',
                      opacity: hasProPlan && !aiGenerationLockActive ? 1 : 0.5
                    }}>
                    <SearchContext.Provider value={{ query, setQuery }}>
                      <Tabs
                        value={value}
                        onValueChange={handleIndexChange}
                        className="w-full">
                        <div className="flex items-center gap-3 mb-6">
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={() =>
                              handleIndexChange(null, Math.max(0, value - 1))
                            }
                            className="h-10 w-10 rounded-lg shrink-0"
                            disabled={!hasProPlan || value === 0}>
                            <ArrowLeft className="h-5 w-5" />
                          </Button>
                          <div className="flex-1 bg-muted/50 rounded-xl px-4 py-2.5 text-center">
                            <span className="text-sm font-medium">
                              {['Client Images', 'Image-to-image'][value]}
                            </span>
                            <span className="text-xs text-muted-foreground ml-2">
                              ({value + 1}/2)
                            </span>
                          </div>
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={() =>
                              handleIndexChange(null, Math.min(1, value + 1))
                            }
                            className="h-10 w-10 rounded-lg shrink-0"
                            disabled={!hasProPlan || value === 1}>
                            <ArrowRight className="h-5 w-5" />
                          </Button>
                        </div>
                        <TabsContent value={value} tabValue={0}>
                          <UnifiedImageGallery
                            source="client-images"
                            setAppImage={handleSelectImage}
                            userId={props.currentClientId}
                          />
                        </TabsContent>
                        <TabsContent value={value} tabValue={1}>
                          <UnifiedImageGallery
                            source="image-to-image"
                            setAppImage={handleSelectImage}
                            userId={props.currentClientId}
                          />
                        </TabsContent>
                      </Tabs>
                    </SearchContext.Provider>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
          {isTikTokPost && aiVideoMode && (
            <div className="lg:col-span-2 flex flex-col gap-6">
              <Card className="shadow-md rounded-2xl border-0 flex flex-col overflow-auto">
                <CardHeader className="pb-4 pt-6 px-6 lg:px-8">
                  <div className="flex items-center gap-4">
                    <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center">
                      <Grid3X3 className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <CardTitle className="text-xl lg:text-2xl font-bold">
                        Select Image
                      </CardTitle>
                      <CardDescription className="text-base mt-1">
                        Choose from your image galleries
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent
                  className="px-6 lg:px-8 pb-8 flex-1"
                  style={{
                    pointerEvents:
                      hasProPlan && !aiGenerationLockActive ? 'auto' : 'none',
                    opacity: hasProPlan && !aiGenerationLockActive ? 1 : 0.5
                  }}>
                  <SearchContext.Provider value={{ query, setQuery }}>
                    <Tabs
                      value={value}
                      onValueChange={handleIndexChange}
                      className="w-full">
                      <div className="flex items-center gap-3 mb-6">
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() =>
                            handleIndexChange(null, Math.max(0, value - 1))
                          }
                          className="h-10 w-10 rounded-lg shrink-0"
                          disabled={!hasProPlan || value === 0}>
                          <ArrowLeft className="h-5 w-5" />
                        </Button>
                        <div className="flex-1 bg-muted/50 rounded-xl px-4 py-2.5 text-center">
                          <span className="text-sm font-medium">
                            {['Client Images', 'Image-to-image'][value]}
                          </span>
                          <span className="text-xs text-muted-foreground ml-2">
                            ({value + 1}/2)
                          </span>
                        </div>
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() =>
                            handleIndexChange(null, Math.min(1, value + 1))
                          }
                          className="h-10 w-10 rounded-lg shrink-0"
                          disabled={!hasProPlan || value === 1}>
                          <ArrowRight className="h-5 w-5" />
                        </Button>
                      </div>
                      <TabsContent value={value} tabValue={0}>
                        <UnifiedImageGallery
                          source="client-images"
                          setAppImage={handleSelectImage}
                          userId={props.currentClientId}
                        />
                      </TabsContent>
                      <TabsContent value={value} tabValue={1}>
                        <UnifiedImageGallery
                          source="image-to-image"
                          setAppImage={handleSelectImage}
                          userId={props.currentClientId}
                        />
                      </TabsContent>
                    </Tabs>
                  </SearchContext.Provider>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
        {/* {isAuthenticated &&
          (currentRole === 'superAdmin' ||
            allowedEmails.includes(currentEmail)) &&
          isOffer && (
            <OfferModal
              onClose={() => {
                setIsOffer(false);
                setIsOriginalVisible(true);
              }}
              onBack={() => {
                setIsOffer(false);
                setIsOriginalVisible(true);
              }}
              props={props}
            />
          )} */}

        {/* Dialog Modals */}
        <Dialog
          open={imageTooSmallModalOpen}
          onOpenChange={setImageTooSmallModalOpen}>
          <DialogContent className="sm:max-w-md text-center py-10">
            <DialogHeader className="items-center">
              <DialogTitle className="text-2xl font-bold text-foreground">
                Error!
              </DialogTitle>
              <DialogDescription className="text-base text-muted-foreground mt-2">
                Your image is too small ({imageDimensions.width} x{' '}
                {imageDimensions.height}), should be at least 800 x 600
              </DialogDescription>
            </DialogHeader>
            <div className="flex justify-center mt-4">
              <Button
                className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 h-11 text-base font-semibold rounded-lg"
                onClick={() => setImageTooSmallModalOpen(false)}>
                OK
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        <Dialog
          open={postSentSuccessModalOpen}
          onOpenChange={setPostSentSuccessModalOpen}>
          <DialogContent className="sm:max-w-md text-center py-10">
            <DialogHeader className="items-center">
              <DialogTitle className="text-2xl font-bold text-foreground">
                Success!
              </DialogTitle>
              <DialogDescription className="text-base text-muted-foreground mt-2">
                Your post has been sent
              </DialogDescription>
            </DialogHeader>
            <div className="flex justify-center mt-4">
              <Button
                className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 h-11 text-base font-semibold rounded-lg"
                onClick={() => setPostSentSuccessModalOpen(false)}>
                OK
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        <Dialog open={postErrorModalOpen} onOpenChange={setPostErrorModalOpen}>
          <DialogContent className="sm:max-w-md text-center py-10">
            <DialogHeader className="items-center">
              <DialogTitle className="text-2xl font-bold text-foreground">
                Error!
              </DialogTitle>
              <DialogDescription className="text-base text-muted-foreground mt-2">
                {postErrorMessage}
              </DialogDescription>
            </DialogHeader>
            <div className="flex justify-center mt-4">
              <Button
                className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 h-11 text-base font-semibold rounded-lg"
                onClick={() => setPostErrorModalOpen(false)}>
                OK
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        <Dialog
          open={noMessageErrorModalOpen}
          onOpenChange={setNoMessageErrorModalOpen}>
          <DialogContent className="sm:max-w-md text-center py-10">
            <DialogHeader className="items-center">
              <DialogTitle className="text-2xl font-bold text-foreground">
                Error!
              </DialogTitle>
              <DialogDescription className="text-base text-muted-foreground mt-2">
                Please add a message
              </DialogDescription>
            </DialogHeader>
            <div className="flex justify-center mt-4">
              <Button
                className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 h-11 text-base font-semibold rounded-lg"
                onClick={() => setNoMessageErrorModalOpen(false)}>
                OK
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        <Dialog
          open={messageValidationErrorModalOpen}
          onOpenChange={setMessageValidationErrorModalOpen}>
          <DialogContent className="sm:max-w-md text-center py-10">
            <DialogHeader className="items-center">
              <DialogTitle className="text-2xl font-bold text-foreground">
                Error!
              </DialogTitle>
              <DialogDescription className="text-base text-muted-foreground mt-2">
                {validationErrorMessage}
              </DialogDescription>
            </DialogHeader>
            <div className="flex justify-center mt-4">
              <Button
                className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 h-11 text-base font-semibold rounded-lg"
                onClick={() => setMessageValidationErrorModalOpen(false)}>
                OK
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        <Dialog
          open={noImageErrorModalOpen}
          onOpenChange={setNoImageErrorModalOpen}>
          <DialogContent className="sm:max-w-md text-center py-10">
            <DialogHeader className="items-center">
              <DialogTitle className="text-2xl font-bold text-foreground">
                Error!
              </DialogTitle>
              <DialogDescription className="text-base text-muted-foreground mt-2">
                Please add a image
              </DialogDescription>
            </DialogHeader>
            <div className="flex justify-center mt-4">
              <Button
                className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 h-11 text-base font-semibold rounded-lg"
                onClick={() => setNoImageErrorModalOpen(false)}>
                OK
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        <Dialog
          open={invalidUrlErrorModalOpen}
          onOpenChange={setInvalidUrlErrorModalOpen}>
          <DialogContent className="sm:max-w-md text-center py-10">
            <DialogHeader className="items-center">
              <DialogTitle className="text-2xl font-bold text-foreground">
                Error!
              </DialogTitle>
              <DialogDescription className="text-base text-muted-foreground mt-2">
                The url is not valid!
              </DialogDescription>
            </DialogHeader>
            <div className="flex justify-center mt-4">
              <Button
                className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 h-11 text-base font-semibold rounded-lg"
                onClick={() => setInvalidUrlErrorModalOpen(false)}>
                OK
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        <Dialog
          open={noImageSelectedErrorModalOpen}
          onOpenChange={setNoImageSelectedErrorModalOpen}>
          <DialogContent className="sm:max-w-md text-center py-10">
            <DialogHeader className="items-center">
              <DialogTitle className="text-2xl font-bold text-foreground">
                Error!
              </DialogTitle>
              <DialogDescription className="text-base text-muted-foreground mt-2">
                No image selected to generate text
              </DialogDescription>
            </DialogHeader>
            <div className="flex justify-center mt-4">
              <Button
                className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 h-11 text-base font-semibold rounded-lg"
                onClick={() => setNoImageSelectedErrorModalOpen(false)}>
                OK
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        <Dialog
          open={textGeneratedSuccessModalOpen}
          onOpenChange={setTextGeneratedSuccessModalOpen}>
          <DialogContent className="sm:max-w-md text-center py-10">
            <DialogHeader className="items-center">
              <DialogTitle className="text-2xl font-bold text-foreground">
                Success!
              </DialogTitle>
              <DialogDescription className="text-base text-muted-foreground mt-2">
                Text generated successfully
              </DialogDescription>
            </DialogHeader>
            <div className="flex justify-center mt-4">
              <Button
                className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 h-11 text-base font-semibold rounded-lg"
                onClick={() => setTextGeneratedSuccessModalOpen(false)}>
                OK
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        <Dialog
          open={aiVideoGeneratedSuccessModalOpen}
          onOpenChange={setAiVideoGeneratedSuccessModalOpen}>
          <DialogContent className="sm:max-w-md text-center py-10">
            <DialogHeader className="items-center">
              <DialogTitle className="text-2xl font-bold text-foreground">
                Success!
              </DialogTitle>
              <DialogDescription className="text-base text-muted-foreground mt-2">
                Video generated successfully. The video is ready to be posted.
              </DialogDescription>
            </DialogHeader>
            <div className="flex justify-center mt-4">
              <Button
                className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 h-11 text-base font-semibold rounded-lg"
                onClick={() => setAiVideoGeneratedSuccessModalOpen(false)}>
                OK
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        <Dialog
          open={textGenerationErrorModalOpen}
          onOpenChange={setTextGenerationErrorModalOpen}>
          <DialogContent className="sm:max-w-md text-center py-10">
            <DialogHeader className="items-center">
              <DialogTitle className="text-2xl font-bold text-foreground">
                Error!
              </DialogTitle>
              <DialogDescription className="text-base text-muted-foreground mt-2">
                The text could not be generated automatically.
              </DialogDescription>
            </DialogHeader>
            <div className="flex justify-center mt-4">
              <Button
                className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 h-11 text-base font-semibold rounded-lg"
                onClick={() => setTextGenerationErrorModalOpen(false)}>
                OK
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </main>
    </>
  );
};

const fileToBase64 = (file, callBack) => {
  file = file.files[0];
  var reader = new FileReader();
  reader.readAsDataURL(file);
  reader.onloadend = function () {
    callBack(reader.result);
  };
  reader.onerror = function (error) {
    console.error('Error: ', error);
  };
};

const blobToBase64 = (blob, callBack) => {
  let xhr = new XMLHttpRequest();
  xhr.responseType = 'blob';
  xhr.onload = function () {
    let recoveredBlob = xhr.response;
    let reader = new FileReader();
    reader.onload = function () {
      callBack(reader.result);
    };
    reader.onerror = function (error) {
      console.error('Error: ', error);
    };
    reader.readAsDataURL(recoveredBlob);
  };
  xhr.open('GET', blob);
  xhr.send();
};

export default NewPost;
